import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import mapboxgl from 'mapbox-gl';
import { debug } from "../lib/debug.js";
import 'mapbox-gl/dist/mapbox-gl.css';
import { useWorldState } from "../components/state/WorldStateContext.jsx";
import { useActiveFlights, AIRPORTS, calculateDistance, calculateFlightTime, calculateEarnings } from "../components/sim/GameLoopEngine.jsx";
import { AIRPORTS as FULL_AIRPORTS } from "../systems/AirportDatabase.js";
import { GLOBAL_AIRPORTS } from "../data/GlobalAirports.js";
import { AIRCRAFT_DATABASE } from "../data/aircraft.js";
import { greatCircleInterpolate } from "../utils/FlightInterpolator.js";
import { SmoothAircraftMarkerMapbox } from "../components/worldmap/SmoothAircraftMarkerMapbox.jsx";
import { intermediatePoint } from "../components/sim/GreatCircleNav.jsx";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Gauge, Fuel } from "lucide-react";
import { canManuallyEndDay } from "../systems/EndOfDaySystem.js";
import SimpleHubAuction from "../components/simple/SimpleHubAuction.jsx";
import ExclusiveHubOffer, { EXCLUSIVE_HUB_LIMIT, HUB_CAPACITY } from "../components/simple/ExclusiveHubOffer.jsx";
import { processAnomalousBehavior, hasAnomalousBehavior, groundAircraft, showSchrodingerCrash } from "../systems/AnomalousBehaviors.js";
import { toast } from "../components/ui/use-toast.jsx";
import FakeBSOD, { useFakeBSOD } from "../components/anomalous/FakeBSOD.jsx";
import TimelineForkModal, { useTimelineFork } from "../components/anomalous/TimelineForkModal.jsx";
import AnomalyModal from "../components/modals/AnomalyModal.jsx";
import { startZombieOutbreak } from "../components/anomalous/ZombieNewsSystem.js";
import { getRouteColor } from "../systems/RouteDemandSystem.js";
import { FuelPriceTicker } from "../components/fuel/FuelPriceTicker.jsx";
import NewsTicker from "../components/ui/NewsTicker.jsx";
import NewRouteModal from "../components/simple/NewRouteModal.jsx";

// Mapbox access token
mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN;

// Tomorrow.io weather API key
const TOMORROW_API_KEY = import.meta.env.VITE_TOMORROW_API_KEY;

// Weather radar toggle (RainViewer) - set to false if causing issues
const ENABLE_WEATHER_RADAR = true;

// Map styles
const MAP_STYLES = {
  'Night': 'mapbox://styles/mapbox/navigation-night-v1',
  'Dark': 'mapbox://styles/mapbox/dark-v11',
  'Day': 'mapbox://styles/mapbox/navigation-day-v1',
  'Light': 'mapbox://styles/mapbox/light-v11',
  'Satellite': 'mapbox://styles/mapbox/satellite-v9',
  'Satellite Streets': 'mapbox://styles/mapbox/satellite-streets-v12',
  'Streets': 'mapbox://styles/mapbox/streets-v12',
};

// Calculate figure-8 (lemniscate) position for Möbius anomalous aircraft
// Returns position that traces a figure-8 centered on the route midpoint
function calculateFigureEightPosition(origin, dest, progress) {
  // Center point of the figure-8
  const centerLat = (origin.lat + dest.lat) / 2;
  const centerLon = (origin.lon + dest.lon) / 2;

  // Scale based on route distance (roughly half the route length for each loop)
  const latSpan = Math.abs(dest.lat - origin.lat);
  const lonSpan = Math.abs(dest.lon - origin.lon);
  const scale = Math.max(latSpan, lonSpan) * 0.4; // 40% of route span for loop size

  // Calculate route angle to orient the figure-8 along the route
  const routeAngle = Math.atan2(dest.lon - origin.lon, dest.lat - origin.lat);

  // Parametric lemniscate of Bernoulli: x = cos(t) / (1 + sin²(t)), y = sin(t)cos(t) / (1 + sin²(t))
  // Map progress [0,1] to t [0, 2π] for full figure-8
  const t = progress * 2 * Math.PI;
  const sinT = Math.sin(t);
  const cosT = Math.cos(t);
  const denom = 1 + sinT * sinT;

  // Raw figure-8 coordinates (normalized -1 to 1)
  const rawX = cosT / denom;
  const rawY = (sinT * cosT) / denom;

  // Rotate to align with route direction
  const rotatedX = rawX * Math.cos(routeAngle) - rawY * Math.sin(routeAngle);
  const rotatedY = rawX * Math.sin(routeAngle) + rawY * Math.cos(routeAngle);

  // Scale and translate to route center
  const lat = centerLat + rotatedY * scale;
  const lon = centerLon + rotatedX * scale;

  return { lat, lon };
}

// Calculate bearing between two points (returns degrees, 0=north, 90=east)
function calculateBearing(lat1, lon1, lat2, lon2) {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const toDeg = (rad) => (rad * 180) / Math.PI;

  const dLon = toRad(lon2 - lon1);
  const lat1Rad = toRad(lat1);
  const lat2Rad = toRad(lat2);

  const y = Math.sin(dLon) * Math.cos(lat2Rad);
  const x = Math.cos(lat1Rad) * Math.sin(lat2Rad) - Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(dLon);

  let bearing = toDeg(Math.atan2(y, x));
  return (bearing + 360) % 360;
}

// Calculate overall flight progress based on phase
function calculateOverallProgress(flight) {
  const phaseWeights = {
    scheduled: 0, boarding: 0, pushback: 0, taxi_out: 0.02,
    takeoff: 0.03, climb: 0.05, cruise: 0.15, descent: 0.85,
    approach: 0.92, landing: 0.96, taxi_in: 0.98, arrived: 1, completed: 1,
  };
  const base = phaseWeights[flight.phase] || 0;
  const phaseOrder = ["boarding", "pushback", "taxi_out", "takeoff", "climb", "cruise", "descent", "approach", "landing", "taxi_in", "arrived"];
  const idx = phaseOrder.indexOf(flight.phase);
  const nextPhase = idx >= 0 && idx < phaseOrder.length - 1 ? phaseOrder[idx + 1] : flight.phase;
  const nextWeight = phaseWeights[nextPhase] || base;
  return base + (nextWeight - base) * (flight.progress || 0);
}

// Get altitude based on phase
function getAltitude(flight) {
  const aircraft = AIRCRAFT_DATABASE[flight.aircraftType] || AIRCRAFT_DATABASE["E175"];
  const maxAlt = aircraft?.ceiling || 41000;
  const cruiseAlt = Math.min(maxAlt, 35000);

  switch (flight.phase) {
    case "boarding":
    case "pushback":
    case "taxi_out":
    case "taxi_in":
    case "arrived":
      return 0;
    case "takeoff":
      return Math.round(1000 + (flight.progress || 0) * 4000);
    case "climb":
      return Math.round(5000 + (flight.progress || 0) * (cruiseAlt - 5000));
    case "cruise":
      return cruiseAlt;
    case "descent":
      return Math.round(cruiseAlt - (flight.progress || 0) * (cruiseAlt - 10000));
    case "approach":
      return Math.round(10000 - (flight.progress || 0) * 7000);
    case "landing":
      return Math.round(3000 - (flight.progress || 0) * 3000);
    default:
      return 0;
  }
}

// Get ground speed based on phase
function getGroundSpeed(flight) {
  const aircraft = AIRCRAFT_DATABASE[flight.aircraftType] || AIRCRAFT_DATABASE["E175"];
  const cruiseSpeed = aircraft?.cruiseSpeed || 450;

  switch (flight.phase) {
    case "boarding":
    case "pushback":
    case "arrived":
      return 0;
    case "taxi_out":
    case "taxi_in":
      return 20; // Constant taxi speed (no random fluctuation)
    case "takeoff":
      return Math.round(80 + (flight.progress || 0) * 170);
    case "climb":
      return Math.round(250 + (flight.progress || 0) * (cruiseSpeed - 280));
    case "cruise":
      return cruiseSpeed; // Constant cruise speed (no random fluctuation)
    case "descent":
      return Math.round(cruiseSpeed - (flight.progress || 0) * 150);
    case "approach":
      return Math.round(280 - (flight.progress || 0) * 130);
    case "landing":
      return Math.round(150 - (flight.progress || 0) * 100);
    default:
      return 0;
  }
}

// Calculate fuel data
function getFuelData(flight, distance) {
  const aircraft = AIRCRAFT_DATABASE[flight.aircraftType] || AIRCRAFT_DATABASE["E175"];
  const fuelBurn = aircraft?.fuelBurn || 2400;
  const fuelCapacity = aircraft?.fuelCapacity || 20000;

  const flightHours = calculateFlightTime(distance, flight.aircraftType);
  const totalFuelNeeded = fuelBurn * flightHours * 1.15;
  const progress = calculateOverallProgress(flight);
  const fuelUsed = Math.round(totalFuelNeeded * progress);
  const fuelRemaining = Math.round(totalFuelNeeded - fuelUsed);

  return {
    burnRate: fuelBurn,
    used: fuelUsed,
    remaining: fuelRemaining,
    capacity: fuelCapacity,
  };
}

// Phase display names
const phaseNames = {
  scheduled: "Scheduled", boarding: "Boarding", pushback: "Pushback",
  taxi_out: "Taxi Out", takeoff: "Takeoff", climb: "Climbing",
  cruise: "Cruise", descent: "Descending", approach: "Approach",
  landing: "Landing", taxi_in: "Taxi In", arrived: "Arrived", completed: "Completed",
};

// Format time in UTC
function formatTime(date) {
  const hours = date.getUTCHours();
  const minutes = date.getUTCMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${displayHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${ampm}`;
}

// Format number with commas
function formatNumber(num) {
  return num.toLocaleString();
}

// Style Switcher Component
function StyleSwitcher({ currentStyle, onStyleChange, t }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div style={{
      position: 'absolute',
      top: 80,
      right: 20,
      zIndex: 1000,
    }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          background: 'rgba(0,0,0,0.8)',
          color: 'white',
          border: '1px solid #333',
          padding: '8px 12px',
          borderRadius: 8,
          cursor: 'pointer',
          fontSize: 12,
        }}
      >
        {t('map.mapStyle')}
      </button>
      {isOpen && (
        <div style={{
          background: 'rgba(0,0,0,0.9)',
          borderRadius: 8,
          padding: 8,
          marginTop: 4,
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}>
          {Object.keys(MAP_STYLES).map(name => (
            <button
              key={name}
              onClick={() => {
                onStyleChange(MAP_STYLES[name]);
                setIsOpen(false);
              }}
              style={{
                background: currentStyle === MAP_STYLES[name] ? '#3b82f6' : '#1f2937',
                color: 'white',
                border: 'none',
                padding: '6px 12px',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: 12,
                textAlign: 'left',
              }}
            >
              {name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function WorldMapPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { worldState, setWorldState, setGameSpeed, togglePause, completeFlight, createFlight, assignAircraftToRoute } = useWorldState();
  const activeFlights = useActiveFlights();
  const [selectedFlightId, setSelectedFlightId] = useState(null);
  const [followingFlight, setFollowingFlight] = useState(null);
  // Weather radar via RainViewer (free, no rate limits)
  const [showWeather, setShowWeather] = useState(true); // Weather ON by default
  // CONTRAILS REMOVED - caused performance issues (constant setData() calls)
  const ballPositionsRef = useRef({});
  const [pendingArrivals, setPendingArrivals] = useState({});
  const [currentStyle, setCurrentStyle] = useState(MAP_STYLES['Night']);

  // Mapbox refs
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  // contrailsRef REMOVED - contrails disabled for performance
  const routeLinesRef = useRef({});

  // MEMORY LEAK FIX: Refs to store event listener callbacks for proper cleanup
  const eventListenersRef = useRef({
    clusterClick: null,
    airportClick: null,
    mapClick: null,
    mouseEnterCluster: null,
    mouseLeaveCluster: null,
    mouseEnterDots: null,
    mouseLeaveDots: null
  });

  const [mapReady, setMapReady] = useState(false);
  const [showHubAuction, setShowHubAuction] = useState(null); // Hub code to auction, or null
  const [hubOfferType, setHubOfferType] = useState('auction'); // 'auction' or 'exclusive'
  const [hubOfferTrigger, setHubOfferTrigger] = useState(null); // 'cash' or 'capacity'
  const overlayRef = useRef(null);

  // Anomalous behavior UI state
  const bsodState = useFakeBSOD();
  const timelineForkState = useTimelineFork();
  const [pendingTimelineForkEarnings, setPendingTimelineForkEarnings] = useState(null);
  const [pendingTimelineForkFlightId, setPendingTimelineForkFlightId] = useState(null);

  // Anomaly modal state (for Passenger Zero, etc.)
  const [anomalyEvent, setAnomalyEvent] = useState(null);

  // Route creation from map click
  const [routeCreationAirport, setRouteCreationAirport] = useState(null);

  // Zero Divided by Zero map haunting state
  const [zeroDividedLocations, setZeroDividedLocations] = useState([]);

  // Check if player has Zero Divided by Zero collected
  const hasZeroDividedByZero = useMemo(() => {
    const collected = worldState?.collectedAircraft || [];
    return collected.includes('zero-divided-by-zero');
  }, [worldState?.collectedAircraft]);

  // Register global function for map popup route creation button
  useEffect(() => {
    window.__createRouteToAirport = (airportCode) => {
      debug('[WorldMap] Creating route to:', airportCode);
      setRouteCreationAirport(airportCode);
    };
    return () => {
      delete window.__createRouteToAirport;
    };
  }, []);

  // Update Zero Divided by Zero positions every 30 seconds
  useEffect(() => {
    if (!hasZeroDividedByZero) {
      setZeroDividedLocations([]);
      return;
    }

    const updatePositions = () => {
      const allAirportCodes = Object.keys(GLOBAL_AIRPORTS);
      const numLocations = 3 + Math.floor(Math.random() * 3); // 3-5 locations
      const newLocations = [];

      for (let i = 0; i < numLocations && allAirportCodes.length > 0; i++) {
        const randomIndex = Math.floor(Math.random() * allAirportCodes.length);
        const code = allAirportCodes[randomIndex];
        const airport = GLOBAL_AIRPORTS[code];
        if (airport && airport.lat && airport.lon) {
          newLocations.push({
            code,
            lat: airport.lat,
            lon: airport.lon,
            city: airport.city || code,
          });
        }
      }

      setZeroDividedLocations(newLocations);
      debug('[WorldMap] Zero Divided by Zero haunting:', newLocations.map(l => l.code));
    };

    // Initial update
    updatePositions();

    // Update every 30 seconds
    const interval = setInterval(updatePositions, 30000);
    return () => clearInterval(interval);
  }, [hasZeroDividedByZero]);

  // ============================================================================
  // AGEE-WHELLS TIME SLIP HANDLING
  // Check for flights that need to disappear (at 50% progress) or reappear (2 days later)
  // ============================================================================
  useEffect(() => {
    // MEMORY LEAK FIX: Skip entirely when no active flights
    const activeFlightCount = (worldState?.flights || []).filter(
      f => f.status !== 'completed' && f.status !== 'cancelled'
    ).length;
    if (activeFlightCount === 0) return;

    const checkTimeSlip = () => {
      const currentDay = worldState?.dayNumber || 1;
      const flights = worldState?.flights || [];

      for (const flight of flights) {
        // Skip completed/cancelled flights
        if (flight.status === 'completed' || flight.status === 'cancelled') continue;

        // Check for flights that need to DISAPPEAR (at 50% progress by default)
        // DEBUG: Log any flight with eosId to see if timeSlipActive is set
        if (flight.eosId === 'agee-whells' || flight.timeSlipActive) {
          debug('[TimeSlip DEBUG]', flight.flightNumber, {
            eosId: flight.eosId,
            timeSlipActive: flight.timeSlipActive,
            timeSlipped: flight.timeSlipped,
            distanceFraction: flight.distanceFraction,
            progress: flight.progress,
          });
        }
        if (flight.timeSlipActive && !flight.timeSlipped) {
          const progress = flight.distanceFraction || 0;
          const disappearThreshold = flight.timeSlipDisappearAt || 0.5;
          if (progress >= disappearThreshold) {
            debug('[TimeSlip] Agee-Whells disappearing at 50%:', flight.flightNumber, 'progress:', progress);

            // Mark flight as timeSlipped (will be hidden from map)
            setWorldState(prev => ({
              ...prev,
              flights: prev.flights.map(f =>
                f.id === flight.id
                  ? { ...f, timeSlipped: true, timeSlipActive: false }
                  : f
              ),
            }));

            // Show disappear modal
            if (flight.timeSlipModalDisappear) {
              setAnomalyEvent({
                title: flight.timeSlipModalDisappear.title,
                message: flight.timeSlipModalDisappear.message,
                type: 'time_slip_disappear',
                aircraftId: 'agee-whells',
              });
            }
          }
        }

        // Check for flights that need to REAPPEAR (2 game days later)
        if (flight.timeSlipped && flight.timeSlipReappearOnDay) {
          if (currentDay >= flight.timeSlipReappearOnDay) {
            debug('[TimeSlip] Agee-Whells reappearing:', flight.flightNumber, 'on day', currentDay);

            // Calculate earnings for the completed flight
            const earnings = flight.preCalculatedEarnings || calculateEarnings(flight, worldState);
            const revenue = earnings?.revenue || 0;

            // Get destination name
            const destAirport = AIRPORTS[flight.destination] || FULL_AIRPORTS[flight.destination] || GLOBAL_AIRPORTS[flight.destination];
            const destName = destAirport?.city || destAirport?.name || flight.destination;

            // Show reappear modal with revenue
            if (flight.timeSlipModalReappear) {
              const message = flight.timeSlipModalReappear.message
                .replace('{destination}', destName)
                .replace('{revenue}', revenue.toLocaleString());

              setAnomalyEvent({
                title: flight.timeSlipModalReappear.title,
                message: message,
                type: 'time_slip_reappear',
                aircraftId: 'agee-whells',
              });
            }

            // Complete the flight
            completeFlight(flight.id, earnings);
          }
        }
      }
    };

    // Check every 500ms for smoother 50% detection
    const interval = setInterval(checkTimeSlip, 500);
    // Also check immediately
    checkTimeSlip();

    return () => clearInterval(interval);
  }, [worldState?.flights, worldState?.dayNumber, setWorldState, completeFlight]);

  // DEBUG: Memory leak monitor - logs counts every 30s
  useEffect(() => {
    const interval = setInterval(() => {
      const completedFlights = worldState?.flights?.filter(f => f.status === 'completed' || f.status === 'cancelled').length || 0;
      const activeFlights = worldState?.flights?.filter(f => f.status !== 'completed' && f.status !== 'cancelled').length || 0;
      const ballPosCount = Object.keys(ballPositionsRef.current).length;
      const pendingCount = Object.keys(pendingArrivals).length;
      debug('[MEMORY CHECK]', {
        completedFlights,
        activeFlights,
        ballPositions: ballPosCount,
        pendingArrivals: pendingCount
      });
    }, 30000);
    return () => clearInterval(interval);
  }, [worldState?.flights, pendingArrivals]);

  // Check for pending hub invites (Simple/Arcade mode only)
  // Decide between exclusive offer (first 3 hubs) or auction (later hubs)
  useEffect(() => {
    const pendingInvites = worldState?.pendingHubInvites || [];
    const isSimpleMode = worldState?.gameMode === 'simple' || worldState?.gameMode === 'arcade';

    // If there's a pending invite and we're not already showing an auction, show it
    if (isSimpleMode && pendingInvites.length > 0 && !showHubAuction) {
      const nextInvite = pendingInvites[0];
      debug('[WorldMap] Hub invite detected:', nextInvite);

      // Decide: exclusive offer or auction?
      // First 3 hubs (including starting hub) get exclusive offers, then auctions
      const exclusiveHubsAwarded = worldState?.exclusiveHubsAwarded || 0;
      const existingHubs = worldState?.airline?.hubs || ['KPDX'];
      const totalHubsIncludingPending = existingHubs.length; // Starting hub counts

      // Use exclusive offer if under the limit
      if (exclusiveHubsAwarded < EXCLUSIVE_HUB_LIMIT) {
        debug(`[WorldMap] Showing EXCLUSIVE offer for ${nextInvite} (${exclusiveHubsAwarded}/${EXCLUSIVE_HUB_LIMIT} exclusive hubs awarded)`);
        setHubOfferType('exclusive');
        setHubOfferTrigger('cash'); // Default trigger, could be 'capacity' from GameLoopEngine
      } else {
        debug(`[WorldMap] Showing AUCTION for ${nextInvite} (${exclusiveHubsAwarded} exclusive hubs already awarded)`);
        setHubOfferType('auction');
      }

      setShowHubAuction(nextInvite);
    }
  }, [worldState?.pendingHubInvites, worldState?.gameMode, showHubAuction, worldState?.exclusiveHubsAwarded, worldState?.airline?.hubs]);

  // Handle hub auction close (dismissed without completing = failed)
  const handleHubAuctionClose = useCallback(() => {
    const currentHub = showHubAuction;
    setShowHubAuction(null);

    if (currentHub) {
      setWorldState(prev => ({
        ...prev,
        pendingHubInvites: (prev.pendingHubInvites || []).filter(h => h !== currentHub),
        airline: {
          ...prev.airline,
          // Mark as failed so we don't get re-invited immediately
          failedHubAuctions: [...(prev.airline?.failedHubAuctions || []), { hub: currentHub, timestamp: Date.now() }],
        },
      }));
    }
  }, [showHubAuction, setWorldState]);

  // Handle auction completion (SimpleHubAuction already sets completedHubAuctions/failedHubAuctions)
  const handleHubAuctionComplete = useCallback((wonHub, winner) => {
    const currentHub = showHubAuction;
    debug('[WorldMap] Hub auction complete:', { wonHub, winner });
    setShowHubAuction(null);

    // Just remove from pending - SimpleHubAuction already recorded the outcome
    if (currentHub) {
      setWorldState(prev => ({
        ...prev,
        pendingHubInvites: (prev.pendingHubInvites || []).filter(h => h !== currentHub),
      }));
    }
  }, [showHubAuction, setWorldState]);

  // Callback to receive ball positions (uses ref to avoid state churn)
  const handlePositionUpdate = useCallback((flightId, lat, lon) => {
    // MEMORY LEAK FIX: Add timestamp for cleanup
    ballPositionsRef.current[flightId] = { lat, lon, timestamp: Date.now() };
  }, []);

  // When ball arrives, complete the flight
  const handleBallArrived = useCallback((flight) => {
    debug('[WorldMap] Ball arrived:', flight.flightNumber);

    // =========================================================================
    // AGEE-WHELLS TIME SLIP - If flight is timeSlipped, don't complete normally
    // It will be completed when it "reappears" 2 days later
    // =========================================================================
    if (flight.timeSlipped) {
      debug('[TimeSlip] Flight is timeSlipped, ignoring arrival:', flight.flightNumber);
      // Clean up but don't complete
      setPendingArrivals(prev => {
        const next = { ...prev };
        delete next[flight.id];
        return next;
      });
      delete ballPositionsRef.current[flight.id];
      return;
    }

    // Clean up pendingArrivals
    setPendingArrivals(prev => {
      const next = { ...prev };
      delete next[flight.id];
      return next;
    });
    // Clean up ballPositions ref
    delete ballPositionsRef.current[flight.id];

    if (flight && completeFlight && flight.phase !== "completed" && flight.status !== "completed") {
      // Use preCalculatedEarnings (Simple mode) or calculate (Simulation mode)
      let earnings = flight.preCalculatedEarnings || calculateEarnings(flight, worldState);

      // =================================================================
      // ANOMALOUS BEHAVIOR CHECK - Process special aircraft behaviors at completion
      // =================================================================
      // Use behaviorId (new), eosId, or aircraftId for anomalous behavior lookup
      const aircraftId = flight.behaviorId || flight.eosId || flight.aircraftId;
      if (hasAnomalousBehavior(aircraftId)) {
        const currentDay = worldState?.dayNumber || 1;
        const gameState = {
          currentDay,
          currentHour: new Date(worldState?.gameTime || Date.now()).getHours(),
          zombieOutbreakStarted: worldState?.zombieOutbreakStarted || false,
        };

        const flightData = {
          ...flight,
          baseRevenue: earnings.revenue,
        };

        const anomalyResult = processAnomalousBehavior(aircraftId, 'complete', flightData, gameState);

        // Apply revenue modifiers
        if (anomalyResult.flightData?.revenueMultiplier) {
          const multiplier = anomalyResult.flightData.revenueMultiplier;
          earnings = {
            ...earnings,
            revenue: Math.round(earnings.revenue * multiplier),
            profit: Math.round(earnings.profit * multiplier),
          };
          debug('[WorldMap] Anomalous revenue multiplier:', multiplier, 'New earnings:', earnings);
        }

        // Process side effects
        if (anomalyResult.sideEffects && anomalyResult.sideEffects.length > 0) {
          for (const effect of anomalyResult.sideEffects) {
            switch (effect.type) {
              case 'toast':
                // Show centered anomaly modal (Passenger Zero, etc.)
                debug('[Anomalous Toast]', effect.message);
                const toastAircraft = AIRCRAFT_DATABASE[flight.aircraftType];
                setTimeout(() => {
                  setAnomalyEvent({
                    title: toastAircraft?.name || 'ANOMALOUS EVENT',
                    message: effect.message,
                    type: 'complete',
                    aircraftId: flight.aircraftType,
                  });
                }, 100);
                break;
              case 'fine':
                // Deduct fine from earnings
                earnings = {
                  ...earnings,
                  profit: earnings.profit - effect.amount,
                };
                debug('[WorldMap] Anomalous fine:', effect.amount);
                toast({
                  title: "FINE ISSUED",
                  description: `$${effect.amount.toLocaleString()} deducted from flight earnings.`,
                  variant: "destructive",
                  duration: 5000,
                });
                break;
              case 'ground_aircraft':
                groundAircraft(effect.aircraftId, effect.days, currentDay);
                toast({
                  title: "Aircraft Grounded",
                  description: `Aircraft grounded for ${effect.days} days.`,
                  variant: "destructive",
                  duration: 5000,
                });
                break;
              case 'add_fuel':
                // Add fuel to reserves
                setWorldState(prev => ({
                  ...prev,
                  fuelReserves: (prev.fuelReserves || 0) + effect.amount,
                }));
                debug('[WorldMap] Anomalous fuel add:', effect.amount);
                toast({
                  title: "Fuel Generated",
                  description: `+${effect.amount.toLocaleString()} gallons added to reserves.`,
                  duration: 4000,
                });
                break;
              case 'revenue':
                // Direct revenue modification (can be negative)
                earnings = {
                  ...earnings,
                  profit: earnings.profit + effect.amount,
                };
                debug('[WorldMap] Anomalous revenue effect:', effect.amount);
                break;
              case 'reduce_interest':
                // Store in worldState for loan system to pick up
                setWorldState(prev => ({
                  ...prev,
                  loanInterestReduction: (prev.loanInterestReduction || 0) + effect.percent,
                }));
                debug('[WorldMap] Anomalous interest reduction:', effect.percent);
                toast({
                  title: "Interest Reduced",
                  description: `Loan interest reduced by ${effect.percent}%.`,
                  duration: 4000,
                });
                break;
              case 'fuel_market_chaos':
                // Set temporary fuel price chaos
                setWorldState(prev => ({
                  ...prev,
                  fuelMarketChaos: {
                    active: true,
                    endDay: currentDay + effect.durationDays,
                    priceMin: effect.priceMin,
                    priceMax: effect.priceMax,
                  },
                }));
                debug('[WorldMap] Fuel market chaos activated:', effect);
                break;
              case 'show_fake_bsod':
                // Show fake BSOD overlay (Null Pointer aircraft)
                bsodState.showBSOD(effect.text);
                debug('[WorldMap] Showing fake BSOD');
                break;
              case 'start_zombie_outbreak':
                // Start zombie outbreak (Passenger Zero aircraft)
                startZombieOutbreak(setWorldState, effect.destination);
                debug('[WorldMap] Starting zombie outbreak at', effect.destination);
                break;
              case 'present_choice':
                // Show timeline fork choice modal
                setPendingTimelineForkEarnings(earnings);
                setPendingTimelineForkFlightId(flight.id);
                timelineForkState.showFork(
                  {
                    choiceA: effect.choiceA,
                    choiceB: effect.choiceB,
                    message: effect.message,
                  },
                  (choice, choiceData) => {
                    // Apply chosen multiplier to earnings
                    const multiplier = choiceData.revenueMultiplier || 1;
                    const modifiedEarnings = {
                      ...earnings,
                      revenue: Math.round(earnings.revenue * multiplier),
                      profit: Math.round(earnings.profit * multiplier),
                    };
                    debug('[WorldMap] Timeline Fork choice:', choice, 'Multiplier:', multiplier);
                    completeFlight(flight.id, modifiedEarnings);
                    setPendingTimelineForkEarnings(null);
                    setPendingTimelineForkFlightId(null);
                  }
                );
                // Return early - don't complete flight yet, wait for choice
                return;
              case 'permanent_ground':
                // Retire aircraft to museum (Spruce Moose, Market Maker after 3 uses, etc.)
                setWorldState(prev => ({
                  ...prev,
                  aircraft: prev.aircraft.map(a =>
                    a.id === effect.aircraftId || a.eosId === effect.aircraftId
                      ? { ...a, status: 'museum', museumSince: new Date().toISOString() }
                      : a
                  ),
                }));
                debug('[WorldMap] Aircraft permanently grounded (museum):', effect.aircraftId);
                if (effect.message) {
                  toast({
                    title: "Retired to Museum",
                    description: effect.message,
                    duration: 5000,
                  });
                }
                break;
              case 'increment_aircraft_use':
                // Track use count for limited-use aircraft (Market Maker, etc.)
                setWorldState(prev => ({
                  ...prev,
                  aircraftUses: {
                    ...(prev.aircraftUses || {}),
                    [effect.aircraftId]: effect.newCount,
                  },
                }));
                debug('[WorldMap] Aircraft use count incremented:', effect.aircraftId, 'to', effect.newCount);
                break;
              case 'duplicate_aircraft':
                // Echo Flight - duplicate the aircraft in fleet
                debug('[WorldMap] Duplicating aircraft:', effect.aircraftId);
                setWorldState(prev => {
                  const sourceAircraft = prev.aircraft.find(a =>
                    a.eosId === effect.aircraftId || a.aircraftId === effect.aircraftId
                  );
                  if (!sourceAircraft) return prev;

                  const duplicate = {
                    ...sourceAircraft,
                    id: `ECHO-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
                    registration: `N${Math.floor(Math.random() * 900) + 100}EC`,
                    name: `${sourceAircraft.name || 'Echo Flight'} #${effect.newCount}`,
                    status: 'available',
                    flightHours: 0,
                    cycles: 0,
                    acquiredAt: new Date().toISOString(),
                  };
                  return {
                    ...prev,
                    aircraft: [...prev.aircraft, duplicate],
                    echoFlightCount: effect.newCount,
                  };
                });
                toast({
                  title: "ECHO RESONANCE",
                  description: `A new Echo Flight has materialized! You now have ${effect.newCount}.`,
                  duration: 5000,
                });
                break;
              case 'redispatch':
                // Auto-redispatch flight (Echo Flight, Infinite Loop behavior)
                debug('[WorldMap] Auto-redispatch requested:', effect.origin, '->', effect.destination, 'isReturn:', effect.isReturnLeg);
                // Store redispatch request for RoutesPanel to pick up
                setWorldState(prev => ({
                  ...prev,
                  pendingRedispatch: {
                    aircraftId: effect.aircraftId,
                    origin: effect.origin,
                    destination: effect.destination,
                    isReturnLeg: effect.isReturnLeg || false,
                    flightNumber: effect.flightNumber,
                    timestamp: Date.now(),
                  },
                }));
                toast({
                  title: effect.isReturnLeg ? "Infinite Loop: Returning" : "Auto-Redispatch",
                  description: `${effect.aircraftId} flying ${effect.origin} → ${effect.destination}`,
                  duration: 4000,
                });
                break;
              case 'show_schrodinger_crash':
                // Schrodinger Jet quantum crash visual
                showSchrodingerCrash();
                debug('[WorldMap] Showing Schrodinger crash visual');
                break;
              case 'trigger_event':
                // The Jinx - trigger individual negative event
                const eventMessages = {
                  weather_delay: "Weather delay encountered!",
                  mechanical_issue: "Mechanical issue reported!",
                  crew_shortage: "Crew shortage!",
                  passenger_complaint: "Passenger complaint filed!",
                  bird_strike: "Bird strike!",
                  fuel_issue: "Fuel system warning!",
                };
                const eventMsg = eventMessages[effect.eventType] || `Event: ${effect.eventType}`;
                debug('[WorldMap] Jinx event triggered:', effect.eventType);
                toast({
                  title: "THE JINX",
                  description: eventMsg,
                  variant: "destructive",
                  duration: 2000,
                });
                // Apply small revenue penalty per event (but flight still completes)
                if (!effect.nonCancelling) {
                  earnings = {
                    ...earnings,
                    profit: Math.round(earnings.profit * 0.95),
                  };
                }
                break;
              case 'trigger_all_events':
                // Probability Storm - EVERYTHING happens
                debug('[WorldMap] Probability Storm: ALL EVENTS');
                toast({
                  title: "PROBABILITY STORM",
                  description: "EVERYTHING is happening!",
                  variant: "destructive",
                  duration: 4000,
                });
                // Major revenue penalty
                earnings = {
                  ...earnings,
                  profit: Math.round(earnings.profit * 0.5),
                };
                break;
              case 'suppress_all_events':
                // Probability Storm - NOTHING happens
                debug('[WorldMap] Probability Storm: NO EVENTS');
                toast({
                  title: "PROBABILITY STORM",
                  description: "Nothing happened. Suspiciously smooth.",
                  duration: 4000,
                });
                // Small bonus for smooth flight
                earnings = {
                  ...earnings,
                  profit: Math.round(earnings.profit * 1.1),
                };
                break;
              default:
                debug('[WorldMap] Unhandled anomalous effect:', effect.type);
            }
          }
        }
      }

      // DEBUG: Detailed revenue logging at completion
      const gameLoopProgress = flight.distanceFraction ?? flight.overallProgress ?? 0;
      if (gameLoopProgress < 0.9) {
        console.warn("[WorldMap] FLIGHT COMPLETING EARLY! Progress:", gameLoopProgress, "Flight:", flight.flightNumber);
      }
      debug("[WorldMap] COMPLETING FLIGHT:", {
        flightId: flight.id,
        flightNumber: flight.flightNumber,
        route: `${flight.origin} - ${flight.destination}`,
        phase: flight.phase,
        distanceFraction: gameLoopProgress,
        usingPreCalc: !!flight.preCalculatedEarnings,
        preCalcData: flight.preCalculatedEarnings ? {
          revenue: flight.preCalculatedEarnings.revenue,
          costs: flight.preCalculatedEarnings.costs,
          profit: flight.preCalculatedEarnings.profit,
          pax: flight.preCalculatedEarnings.passengers,
          distance: flight.preCalculatedEarnings.distance,
        } : 'N/A (using calculateEarnings)',
        finalEarnings: {
          revenue: earnings.revenue,
          costs: earnings.costs,
          profit: earnings.profit,
        },
        currentCash: worldState?.airline?.cash,
        expectedCashAfter: (worldState?.airline?.cash || 0) + earnings.profit,
      });

      completeFlight(flight.id, earnings);
    } else {
      debug("[WorldMap] Skipping completion:", flight?.id, flight?.phase, flight?.status);
    }
  }, [completeFlight, worldState]);

  // BUG-007 FIX: Use GameLoopEngine's distanceFraction as source of truth
  // Previously calculated from ball position using Euclidean distance which was inaccurate
  const getBallProgress = useCallback((flight) => {
    // distanceFraction from GameLoopEngine is authoritative
    return flight.distanceFraction ?? flight.overallProgress ?? 0;
  }, []);

  // Derive phase from ball progress - MUST match GameLoopEngine.jsx thresholds!
  // GameLoopEngine is source of truth, no taxi_out phase for active flights
  const getBallPhase = useCallback((flight) => {
    // Use flight.phase directly if available and valid (from GameLoopEngine)
    if (flight.phase && ["takeoff", "climb", "cruise", "descent", "approach", "landing", "taxi_in", "arrived", "completed"].includes(flight.phase)) {
      return flight.phase;
    }
    // Fallback: derive from progress (should match GameLoopEngine.getPhaseFromDistanceFraction)
    const progress = getBallProgress(flight);
    if (progress < 0.08) return "takeoff";
    if (progress < 0.20) return "climb";
    if (progress < 0.80) return "cruise";
    if (progress < 0.92) return "descent";
    if (progress < 0.97) return "approach";
    if (progress < 0.995) return "landing";
    if (progress < 1) return "taxi_in";
    return "arrived";
  }, [getBallProgress]);

  // Airport positions for markers (must be before addAirportsLayer)
  // Now uses GLOBAL_AIRPORTS (4,800+ airports) with clustering
  const airportPositions = useMemo(() => {
    // Build set of active airport codes for quick lookup
    const activeAirportCodes = new Set();
    activeFlights.forEach(flight => {
      activeAirportCodes.add(flight.origin);
      activeAirportCodes.add(flight.destination);
    });

    // Use GLOBAL_AIRPORTS as the primary source (4,800+ airports)
    return Object.entries(GLOBAL_AIRPORTS).map(([code, airport]) => ({
      code,
      ...airport,
      isActive: activeAirportCodes.has(code),
      // Mark major hubs
      isMajor: airport.hub_tier === 'major',
      isLarge: airport.hub_tier === 'large',
    }));
  }, [activeFlights]);

  // Convert airports to GeoJSON (must be before addAirportsLayer)
  const airportsGeoJSON = useMemo(() => ({
    type: 'FeatureCollection',
    features: airportPositions.map(airport => ({
      type: 'Feature',
      properties: {
        code: airport.code,
        name: airport.name || airport.code,
        city: airport.city || '',
        country: airport.country || '',
        hub_tier: airport.hub_tier || 'small',
        isActive: airport.isActive,
        isMajor: airport.isMajor,
        isLarge: airport.isLarge,
      },
      geometry: {
        type: 'Point',
        coordinates: [airport.lon, airport.lat] // [LONGITUDE, LATITUDE] for Mapbox!
      }
    }))
  }), [airportPositions]);

  // MEMORY LEAK FIX: Clean up airport event listeners
  const cleanupAirportEventListeners = useCallback((map) => {
    if (!map) return;

    const listeners = eventListenersRef.current;

    // Remove all airport-related event listeners if they exist
    if (listeners.clusterClick) {
      map.off('click', 'airport-clusters', listeners.clusterClick);
      listeners.clusterClick = null;
    }
    if (listeners.airportClick) {
      map.off('click', 'airport-dots', listeners.airportClick);
      listeners.airportClick = null;
    }
    // NOTE: mapClick is NOT cleaned up here - it's a global handler for clearing followingFlight
    // and should only be removed on unmount, not when airport layers are refreshed
    if (listeners.mouseEnterCluster) {
      map.off('mouseenter', 'airport-clusters', listeners.mouseEnterCluster);
      listeners.mouseEnterCluster = null;
    }
    if (listeners.mouseLeaveCluster) {
      map.off('mouseleave', 'airport-clusters', listeners.mouseLeaveCluster);
      listeners.mouseLeaveCluster = null;
    }
    if (listeners.mouseEnterDots) {
      map.off('mouseenter', 'airport-dots', listeners.mouseEnterDots);
      listeners.mouseEnterDots = null;
    }
    if (listeners.mouseLeaveDots) {
      map.off('mouseleave', 'airport-dots', listeners.mouseLeaveDots);
      listeners.mouseLeaveDots = null;
    }
  }, []);

  // Add airports layer to map with CLUSTERING for 4,800+ airports
  const addAirportsLayer = useCallback((map) => {
    // MEMORY LEAK FIX: Clean up existing event listeners first
    cleanupAirportEventListeners(map);

    // Remove existing layers and source
    ['airport-clusters', 'airport-cluster-count', 'airport-dots'].forEach(layer => {
      if (map.getLayer(layer)) map.removeLayer(layer);
    });
    if (map.getSource('airports')) map.removeSource('airports');

    // Add source with clustering enabled
    map.addSource('airports', {
      type: 'geojson',
      data: airportsGeoJSON,
      cluster: true,
      clusterMaxZoom: 6,    // Cluster until zoom level 6
      clusterRadius: 50,    // Radius in pixels for clustering
    });

    // CLUSTER CIRCLES - shown when airports are clustered
    map.addLayer({
      id: 'airport-clusters',
      type: 'circle',
      source: 'airports',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': [
          'step',
          ['get', 'point_count'],
          '#51bbd6',   // < 10 airports
          10, '#3b82f6',  // 10-50
          50, '#2563eb',  // 50-100
          100, '#1d4ed8', // 100+
        ],
        'circle-radius': [
          'step',
          ['get', 'point_count'],
          15,    // < 10 airports
          10, 20,  // 10-50
          50, 25,  // 50-100
          100, 30, // 100+
        ],
        'circle-stroke-width': 2,
        'circle-stroke-color': '#1e3a5f',
      }
    });

    // CLUSTER COUNT - number inside cluster circles
    map.addLayer({
      id: 'airport-cluster-count',
      type: 'symbol',
      source: 'airports',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': ['get', 'point_count_abbreviated'],
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 12,
      },
      paint: {
        'text-color': '#ffffff',
      }
    });

    // INDIVIDUAL AIRPORTS - shown when zoomed in (unclustered)
    map.addLayer({
      id: 'airport-dots',
      type: 'circle',
      source: 'airports',
      filter: ['!', ['has', 'point_count']], // Only unclustered points
      paint: {
        'circle-radius': [
          'case',
          ['get', 'isMajor'], 8,      // Major hubs
          ['get', 'isLarge'], 6,      // Large airports
          ['get', 'isActive'], 5,     // Active (in use)
          5                            // Small airports - increased from 3px for clickability
        ],
        'circle-color': [
          'case',
          ['get', 'isActive'], '#22c55e',  // Green for active
          ['get', 'isMajor'], '#f59e0b',   // Amber for major hubs
          ['get', 'isLarge'], '#0ea5e9',   // Blue for large
          '#64748b'                         // Gray for others
        ],
        'circle-stroke-width': [
          'case',
          ['get', 'isMajor'], 2,
          ['get', 'isLarge'], 1.5,
          1
        ],
        'circle-stroke-color': [
          'case',
          ['get', 'isActive'], '#16a34a',
          ['get', 'isMajor'], '#d97706',
          '#475569'
        ]
      }
    });

    // MEMORY LEAK FIX: Store event listener callbacks for proper cleanup
    const listeners = eventListenersRef.current;

    // Click handler for clusters - zoom in
    listeners.clusterClick = (e) => {
      const features = map.queryRenderedFeatures(e.point, { layers: ['airport-clusters'] });
      const clusterId = features[0].properties.cluster_id;
      map.getSource('airports').getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) return;
        map.easeTo({
          center: features[0].geometry.coordinates,
          zoom: zoom
        });
      });
    };
    map.on('click', 'airport-clusters', listeners.clusterClick);

    // Click handler for individual airports
    listeners.airportClick = (e) => {
      const props = e.features[0].properties;
      debug('[WorldMap] Clicked airport:', props.code);

      // Get full airport data for runway info
      const airportData = GLOBAL_AIRPORTS[props.code];
      const runwayFt = airportData?.runwayLength || 'Unknown';
      const elevation = airportData?.elevation || 0;

      const popupContent = `
        <div style="color:#000;padding:8px;min-width:200px;">
          <strong style="font-size:14px;">${props.code}</strong>${props.city ? ` - ${props.city}` : ''}
          <br/><span style="font-size:11px;color:#666">${props.name}</span>
          <br/><span style="font-size:10px;color:#888">${props.country} | ${props.hub_tier}</span>
          <br/><span style="font-size:10px;color:#666">Runway: ${typeof runwayFt === 'number' ? runwayFt.toLocaleString() + ' ft' : runwayFt} | Elev: ${elevation.toLocaleString()} ft</span>
          <br/>
          <button
            onclick="window.__createRouteToAirport && window.__createRouteToAirport('${props.code}')"
            style="
              margin-top:8px;
              padding:6px 12px;
              background:linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
              color:white;
              border:none;
              border-radius:6px;
              cursor:pointer;
              font-size:12px;
              font-weight:500;
              width:100%;
            "
            onmouseover="this.style.background='linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)'"
            onmouseout="this.style.background='linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'"
          >
            Create Route Here
          </button>
        </div>
      `;

      new mapboxgl.Popup()
        .setLngLat(e.lngLat)
        .setHTML(popupContent)
        .addTo(map);
    };
    map.on('click', 'airport-dots', listeners.airportClick);

    // Cursor change on hover - store individual callbacks
    listeners.mouseEnterCluster = () => {
      map.getCanvas().style.cursor = 'pointer';
    };
    listeners.mouseLeaveCluster = () => {
      map.getCanvas().style.cursor = '';
    };
    listeners.mouseEnterDots = () => {
      map.getCanvas().style.cursor = 'pointer';
    };
    listeners.mouseLeaveDots = () => {
      map.getCanvas().style.cursor = '';
    };

    map.on('mouseenter', 'airport-clusters', listeners.mouseEnterCluster);
    map.on('mouseleave', 'airport-clusters', listeners.mouseLeaveCluster);
    map.on('mouseenter', 'airport-dots', listeners.mouseEnterDots);
    map.on('mouseleave', 'airport-dots', listeners.mouseLeaveDots);
  }, [airportsGeoJSON, cleanupAirportEventListeners]);

  // RainViewer radar timestamp (updated periodically)
  const [radarTimestamp, setRadarTimestamp] = useState(null);

  // Fetch latest RainViewer radar timestamp - DISABLED (BUG-004)
  useEffect(() => {
    if (!ENABLE_WEATHER_RADAR) return; // Weather radar disabled

    const fetchRadarTimestamp = async () => {
      try {
        const response = await fetch('https://api.rainviewer.com/public/weather-maps.json');
        const data = await response.json();
        // Get the most recent radar frame
        const latestFrame = data.radar?.past?.slice(-1)[0];
        if (latestFrame?.path) {
          setRadarTimestamp(latestFrame.path);
          debug('[WorldMap] RainViewer radar timestamp:', latestFrame.path);
        }
      } catch (err) {
        console.error('[WorldMap] Failed to fetch RainViewer timestamp:', err);
      }
    };

    fetchRadarTimestamp();
    // Refresh timestamp every 5 minutes
    const interval = setInterval(fetchRadarTimestamp, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Add weather radar layer to map (RainViewer) - DISABLED (BUG-004)
  const addWeatherLayer = useCallback((map) => {
    if (!ENABLE_WEATHER_RADAR) return; // Weather radar disabled

    if (!radarTimestamp) {
      console.warn('[WorldMap] No radar timestamp yet, skipping weather layer');
      return;
    }

    // Remove existing if present
    if (map.getLayer('weather-radar-layer')) map.removeLayer('weather-radar-layer');
    if (map.getSource('weather-radar')) map.removeSource('weather-radar');

    try {
      // RainViewer tile URL - direct call, no proxy needed (free tier, no auth)
      // Color scheme 6 = rainbow, size 256, smooth=1, snow=1
      const tileUrl = `https://tilecache.rainviewer.com${radarTimestamp}/256/{z}/{x}/{y}/6/1_1.png`;

      map.addSource('weather-radar', {
        type: 'raster',
        tiles: [tileUrl],
        tileSize: 256
      });

      // Add weather layer ABOVE base map but BELOW our custom layers
      // Insert before 'airport-clusters' if it exists, otherwise add on top
      const beforeLayer = map.getLayer('airport-clusters') ? 'airport-clusters' : undefined;

      map.addLayer({
        id: 'weather-radar-layer',
        type: 'raster',
        source: 'weather-radar',
        paint: {
          'raster-opacity': 0.7
        }
      }, beforeLayer);

      debug('[WorldMap] RainViewer radar layer added');
    } catch (err) {
      console.error('[WorldMap] Failed to add weather layer:', err);
    }
  }, [radarTimestamp]);

  // Handle style change (must be after addAirportsLayer)
  const handleStyleChange = useCallback((newStyle) => {
    const map = mapRef.current;
    if (!map) return;

    // Store current route lines before style change
    const savedRouteLines = { ...routeLinesRef.current };

    map.setStyle(newStyle);
    setCurrentStyle(newStyle);

    // Re-add layers after style change (order: weather -> airports -> routes -> planes)
    map.once('style.load', () => {
      // Weather first (bottom layer)
      if (showWeather) {
        addWeatherLayer(map);
      }
      // Then airports on top of weather
      addAirportsLayer(map);

      // Re-add route lines with great-circle paths
      Object.entries(savedRouteLines).forEach(([flightId, coords]) => {
        if (!coords || typeof coords !== 'object') return;
        const { originLon, originLat, destLon, destLat, originCode, destCode } = coords;
        if (originLon == null || originLat == null || destLon == null || destLat == null) return;

        const sourceId = `route-${flightId}`;
        const layerId = `route-layer-${flightId}`;

        // Generate great-circle path with antimeridian handling
        const segments = 50;
        const rawCoords = [];
        for (let i = 0; i <= segments; i++) {
          const fraction = i / segments;
          const point = intermediatePoint(originLat, originLon, destLat, destLon, fraction);
          rawCoords.push([point.lon, point.lat]);
        }

        // Normalize longitudes for antimeridian crossings
        const routeCoords = [];
        let prevLon = rawCoords[0][0];
        for (let i = 0; i < rawCoords.length; i++) {
          let [lon, lat] = rawCoords[i];
          while (lon - prevLon > 180) lon -= 360;
          while (lon - prevLon < -180) lon += 360;
          routeCoords.push([lon, lat]);
          prevLon = lon;
        }

        map.addSource(sourceId, {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: routeCoords
            }
          }
        });

        // Get demand-based color (green = high demand, red = saturated)
        const routeColor = (originCode && destCode)
          ? getRouteColor(originCode, destCode, worldState?.routeDemand || {})
          : '#f97316'; // fallback orange

        map.addLayer({
          id: layerId,
          type: 'line',
          source: sourceId,
          paint: {
            'line-color': routeColor,
            'line-width': 2,
            'line-opacity': 0.7
          }
        });
      });
    });
  }, [addAirportsLayer, addWeatherLayer, showWeather]);

  // Initialize Mapbox map (only once on mount)
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: currentStyle,
      center: [-122.58, 45.59], // Default to KPDX area [lon, lat]
      zoom: 4,
      minZoom: 1.5,
      maxZoom: 18,
      dragRotate: false,
      pitchWithRotate: false,
      touchPitch: false,
    });

    map.on('load', () => {
      debug('[WorldMap] Mapbox map loaded');
      setMapReady(true);
      // Initial airports layer will be added by the airportsGeoJSON effect
    });

    // MEMORY LEAK FIX: Store map click callback for proper cleanup
    // Clicking anywhere on the map stops camera follow, but keeps route line visible
    // Route line only clears when a different flight is selected
    eventListenersRef.current.mapClick = () => {
      setFollowingFlight(null);
    };
    map.on('click', eventListenersRef.current.mapClick);

    mapRef.current = map;

    return () => {
      // MEMORY LEAK FIX: Clean up event listeners before removing map
      cleanupAirportEventListeners(map);
      // Clean up mapClick separately (not in cleanupAirportEventListeners)
      if (eventListenersRef.current.mapClick) {
        map.off('click', eventListenersRef.current.mapClick);
        eventListenersRef.current.mapClick = null;
      }
      map.remove();
      mapRef.current = null;
    };
  }, [cleanupAirportEventListeners]); // Include cleanup function

  // Center map on player's hub when worldState loads
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    const hub = worldState?.airline?.hub;
    if (!hub) return;

    // Get hub coordinates from airport databases
    const hubAirport = AIRPORTS[hub] || FULL_AIRPORTS[hub] || GLOBAL_AIRPORTS[hub];
    if (hubAirport && hubAirport.lat && hubAirport.lon) {
      debug(`[WorldMap] Centering on hub: ${hub} at [${hubAirport.lon}, ${hubAirport.lat}]`);
      map.flyTo({
        center: [hubAirport.lon, hubAirport.lat],
        zoom: 4,
        duration: 1500,
      });
    }
  }, [mapReady, worldState?.airline?.hub]); // Re-run when hub changes

  // Update or create airports layer when data changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    // Wait for style to load
    const setupAirports = () => {
      const source = map.getSource('airports');
      if (source) {
        // Update existing source
        source.setData(airportsGeoJSON);
      } else {
        // Create new source and layer
        addAirportsLayer(map);
      }

      // Also add weather layer after airports (if weather is enabled and timestamp available)
      // This ensures correct layer order on initial load
      if (showWeather && radarTimestamp && !map.getLayer('weather-radar-layer')) {
        addWeatherLayer(map);
      }
    };

    if (map.isStyleLoaded()) {
      setupAirports();
    } else {
      map.once('load', setupAirports);
    }
  }, [airportsGeoJSON, addAirportsLayer, showWeather, radarTimestamp, addWeatherLayer]);

  // Toggle weather layer visibility and update when timestamp changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    if (!ENABLE_WEATHER_RADAR) return; // Skip if weather radar globally disabled

    const updateWeather = () => {
      const existingLayer = map.getLayer('weather-radar-layer');

      if (showWeather && radarTimestamp) {
        if (existingLayer) {
          // Layer exists - just make it visible
          map.setLayoutProperty('weather-radar-layer', 'visibility', 'visible');
        } else {
          // Layer doesn't exist - add it
          addWeatherLayer(map);
        }
      } else {
        // Hide layer (don't remove it, just hide)
        if (existingLayer) {
          map.setLayoutProperty('weather-radar-layer', 'visibility', 'none');
        }
      }
    };

    // Use idle event to ensure map is fully ready (tiles loaded, style applied)
    if (map.isStyleLoaded()) {
      // Small delay to ensure airports layer is added first on initial load
      const timeoutId = setTimeout(updateWeather, 100);
      return () => clearTimeout(timeoutId);
    } else {
      map.once('style.load', updateWeather);
    }
  }, [showWeather, mapReady, radarTimestamp, addWeatherLayer]);

  // DIRECT weather toggle - immediately update layer visibility when showWeather changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const layerId = 'weather-radar-layer';

    // Check if layer exists and update visibility
    if (map.getLayer(layerId)) {
      map.setLayoutProperty(layerId, 'visibility', showWeather ? 'visible' : 'none');
      debug(`[WorldMap] Weather layer visibility: ${showWeather ? 'visible' : 'none'}`);
    }
  }, [showWeather]);

  // Track previous flights ref
  const prevFlightsRef = useRef({});

  // Calculate flight positions
  const flightPositions = useMemo(() => {
    return activeFlights.map((flight) => {
      const origin = AIRPORTS[flight.origin] || FULL_AIRPORTS[flight.origin] || GLOBAL_AIRPORTS[flight.origin];
      const dest = AIRPORTS[flight.destination] || FULL_AIRPORTS[flight.destination] || GLOBAL_AIRPORTS[flight.destination];
      if (!origin || !dest) return null;

      const airbornePhases = ["takeoff", "climb", "cruise", "descent", "approach", "landing"];
      // BUG FIX: Use distanceFraction (GameLoop's authoritative source) for camera tracking
      // This ensures handleFlightClick centers on the same position as the ball marker
      const overallProgress = flight.distanceFraction ?? calculateOverallProgress(flight);

      // BUG-3 FIX: Guard against NaN progress values that would corrupt position calculation
      const safeProgress = Number.isFinite(overallProgress) ? Math.min(1, Math.max(0, overallProgress)) : 1;

      // Check for special animation types (e.g., Möbius figure-8, VTOnly vertical)
      let pos, aheadPos;
      if (flight.animation === "vertical") {
        // VTOnly: Flies straight up and down, stays at origin position
        pos = { lat: origin.lat, lon: origin.lon };
        aheadPos = { lat: origin.lat, lon: origin.lon };
      } else if (flight.animation === "figure_eight") {
        try {
          pos = calculateFigureEightPosition(origin, dest, safeProgress);
          const lookAheadProgress = Math.min(1, safeProgress + 0.01);
          aheadPos = calculateFigureEightPosition(origin, dest, lookAheadProgress);
        } catch (e) {
          console.error("[WorldMap] Figure-8 calculation failed for", flight.id, e);
          // Fallback to normal path
          pos = greatCircleInterpolate(origin.lat, origin.lon, dest.lat, dest.lon, safeProgress);
          const lookAheadProgress = Math.min(1, safeProgress + 0.01);
          aheadPos = greatCircleInterpolate(origin.lat, origin.lon, dest.lat, dest.lon, lookAheadProgress);
        }
      } else {
        // Normal great circle interpolation
        pos = greatCircleInterpolate(origin.lat, origin.lon, dest.lat, dest.lon, safeProgress);
        const lookAheadProgress = Math.min(1, safeProgress + 0.01);
        aheadPos = greatCircleInterpolate(origin.lat, origin.lon, dest.lat, dest.lon, lookAheadProgress);
      }
      const bearing = calculateBearing(pos.lat, pos.lon, aheadPos.lat, aheadPos.lon);
      const distance = calculateDistance(flight.origin, flight.destination);

      // BUG-3 FIX: Skip flights with NaN position or distance
      if (!Number.isFinite(pos.lat) || !Number.isFinite(pos.lon) || !Number.isFinite(distance)) {
        return null;
      }

      return {
        ...flight,
        currentLat: pos.lat,
        currentLon: pos.lon,
        originLat: origin.lat,
        originLon: origin.lon,
        destLat: dest.lat,
        destLon: dest.lon,
        bearing,
        overallProgress,
        distance,
        distanceRemaining: Math.round(distance * (1 - overallProgress)),
        isAirborne: airbornePhases.includes(flight.phase),
        altitude: getAltitude(flight),
        groundSpeed: getGroundSpeed(flight),
        fuel: getFuelData(flight, distance),
      };
    }).filter(Boolean);
  }, [activeFlights]);

  // Handle trackFlightId from navigation state (TRACK button from RoutesPanel)
  // Programmatically clicks the ball marker DOM element — exact same code path as user click
  useEffect(() => {
    const trackFlightId = location.state?.trackFlightId;
    if (!trackFlightId || !mapReady) return;

    // Clear navigation state immediately so it doesn't re-trigger
    navigate(location.pathname, { replace: true, state: {} });

    // Wait for ball markers to render, then click the matching one
    const tryClick = (attempts = 0) => {
      const el = document.querySelector(`[data-flight-id="${trackFlightId}"]`);
      if (el) {
        el.click();
        return;
      }
      if (attempts < 10) {
        setTimeout(() => tryClick(attempts + 1), 200);
      }
    };
    setTimeout(() => tryClick(), 300);
  }, [location.state?.trackFlightId, mapReady, navigate, location.pathname]);

  // When a flight is removed from activeFlights, add to pendingArrivals to keep ball animating
  // EXCEPTION: Emergency landed / crashed flights should NOT be added - they despawn immediately
  useEffect(() => {
    const currentIds = new Set(flightPositions.map(f => f.id));
    const prevFlights = prevFlightsRef.current;
    const map = mapRef.current;

    // Find flights that were in previous render but not in current
    Object.keys(prevFlights).forEach(id => {
      if (!currentIds.has(id) && prevFlights[id] && !pendingArrivals[id]) {
        // Check if this flight is emergency_landed or crashed - DON'T add to pending, despawn immediately
        const actualFlight = worldState?.flights?.find(f => f.id === id);
        const isEmergency = actualFlight?.status === 'emergency_landed' ||
                            actualFlight?.status === 'crashed' ||
                            actualFlight?.phase === 'emergency_landed' ||
                            actualFlight?.phase === 'crashed';

        if (isEmergency) {
          debug('[WorldMap] Emergency flight removed - despawning immediately:', id);
          // Clean up route line for this flight
          if (map && map.getStyle()) {
            const routeSourceId = `route-${id}`;
            const routeLayerId = `route-layer-${id}`;
            if (map.getLayer(routeLayerId)) map.removeLayer(routeLayerId);
            if (map.getSource(routeSourceId)) map.removeSource(routeSourceId);
          }
          delete routeLinesRef.current[id];
          delete ballPositionsRef.current[id];
          return; // Don't add to pendingArrivals
        }

        // Normal flight completion - add to pending so ball keeps animating to destination
        // Include timestamp for timeout-based cleanup as a fallback
        setPendingArrivals(prev => ({
          ...prev,
          [id]: { ...prevFlights[id], _addedAt: Date.now() }
        }));
      }
    });

    // Update ref with current flights for next comparison
    const currentFlights = {};
    flightPositions.forEach(f => { currentFlights[f.id] = f; });
    prevFlightsRef.current = currentFlights;
  }, [flightPositions, pendingArrivals, worldState?.flights]);

  // Cleanup stale pendingArrivals - remove any flights that are emergency_landed or no longer exist
  useEffect(() => {
    const map = mapRef.current;
    const staleIds = [];

    Object.keys(pendingArrivals).forEach(id => {
      const actualFlight = worldState?.flights?.find(f => f.id === id);
      // Remove ONLY if: flight doesn't exist, or is emergency/crashed/cancelled
      // DO NOT remove for completed flights - let the marker animation finish and call onArrived!
      // The marker's onArrived callback cleans itself up from pendingArrivals.
      // Timeout fallback (30 sec) catches any flights where onArrived never fired
      const pendingEntry = pendingArrivals[id];
      const isTimedOut = pendingEntry?._addedAt && (Date.now() - pendingEntry._addedAt > 30000);
      // NOTE: Completed flights are NOT removed here - they're cleaned by onArrived callback
      // Removing them here causes a double-flash visual glitch
      const shouldRemove = !actualFlight ||
                           actualFlight.status === 'emergency_landed' ||
                           actualFlight.status === 'crashed' ||
                           actualFlight.status === 'cancelled' ||
                           isTimedOut;

      if (shouldRemove) {
        if (isTimedOut) {
          console.warn('[WorldMap] PendingArrival timed out (30s) - marker failed to call onArrived:', id);
        }
        staleIds.push(id);
        // Clean up map layers - guard against map being destroyed or not ready
        if (map && map.getStyle()) {
          const routeSourceId = `route-${id}`;
          const routeLayerId = `route-layer-${id}`;
          if (map.getLayer(routeLayerId)) map.removeLayer(routeLayerId);
          if (map.getSource(routeSourceId)) map.removeSource(routeSourceId);
        }
        delete routeLinesRef.current[id];
        delete ballPositionsRef.current[id];
      }
    });

    if (staleIds.length > 0) {
      debug('[WorldMap] Cleaning up stale pendingArrivals:', staleIds);
      setPendingArrivals(prev => {
        const next = { ...prev };
        staleIds.forEach(id => delete next[id]);
        return next;
      });
    }
  }, [worldState?.flights, pendingArrivals]);

  // CONTRAILS REMOVED - caused performance issues (constant setData() calls per flight)
  // Route lines remain for showing flight paths

  // Route line helper functions - generates great-circle path
  const addRouteLine = useCallback((map, flightId, originLon, originLat, destLon, destLat, originCode = null, destCode = null) => {
    const sourceId = `route-${flightId}`;
    const layerId = `route-layer-${flightId}`;

    // Skip if already exists
    if (map.getSource(sourceId)) return;

    // Validate coordinates - skip if any are invalid
    if (!Number.isFinite(originLon) || !Number.isFinite(originLat) ||
        !Number.isFinite(destLon) || !Number.isFinite(destLat)) {
      console.warn(`[WorldMap] Invalid coordinates for route ${flightId}:`, { originLon, originLat, destLon, destLat });
      return;
    }

    // Generate great-circle path with more segments for accuracy
    // More segments help with polar routes and antimeridian crossings
    const segments = 50;
    const rawCoordinates = [];
    for (let i = 0; i <= segments; i++) {
      const fraction = i / segments;
      const point = intermediatePoint(originLat, originLon, destLat, destLon, fraction);
      // Validate each point
      if (!Number.isFinite(point.lon) || !Number.isFinite(point.lat)) continue;
      rawCoordinates.push([point.lon, point.lat]); // GeoJSON uses [lon, lat]
    }

    // Skip if no valid coordinates generated
    if (rawCoordinates.length < 2) {
      console.warn(`[WorldMap] Not enough valid coordinates for route ${flightId}`);
      return;
    }

    // Handle antimeridian crossing: normalize longitudes to prevent horizontal lines
    // When consecutive points cross the ±180° boundary, adjust to continuous range
    const coordinates = [];
    let prevLon = rawCoordinates[0][0];
    for (let i = 0; i < rawCoordinates.length; i++) {
      let [lon, lat] = rawCoordinates[i];
      // If longitude jumped more than 180°, adjust it
      while (lon - prevLon > 180) lon -= 360;
      while (lon - prevLon < -180) lon += 360;
      coordinates.push([lon, lat]);
      prevLon = lon;
    }

    map.addSource(sourceId, {
      type: 'geojson',
      data: {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: coordinates
        }
      }
    });

    // Get demand-based color (green = high demand, red = saturated)
    const routeColor = (originCode && destCode)
      ? getRouteColor(originCode, destCode, worldState?.routeDemand || {})
      : '#f97316'; // fallback orange

    map.addLayer({
      id: layerId,
      type: 'line',
      source: sourceId,
      paint: {
        'line-color': routeColor,
        'line-width': 2,
        'line-opacity': 0.7
      }
    });

    // MEMORY LEAK FIX: Add timestamp for cleanup
    routeLinesRef.current[flightId] = {
      originLon, originLat, destLon, destLat, originCode, destCode,
      timestamp: Date.now()
    };
  }, [worldState?.routeDemand]);

  const removeRouteLine = useCallback((map, flightId) => {
    const sourceId = `route-${flightId}`;
    const layerId = `route-layer-${flightId}`;

    if (map.getLayer(layerId)) {
      map.removeLayer(layerId);
    }
    if (map.getSource(sourceId)) {
      map.removeSource(sourceId);
    }
    delete routeLinesRef.current[flightId];
  }, []);

  // MEMORY LEAK FIX: Prune route lines by age and size
  const pruneRouteLines = useCallback((map) => {
    if (!map) return;

    const now = Date.now();
    const MAX_ROUTE_LINES = 100;  // Cap at 100 active route lines
    const MAX_AGE_MS = 5 * 60 * 1000;  // Remove lines older than 5 minutes

    const entries = Object.entries(routeLinesRef.current);

    // Remove old entries first
    entries.forEach(([flightId, data]) => {
      if (data.timestamp && (now - data.timestamp) > MAX_AGE_MS) {
        removeRouteLine(map, flightId);
      }
    });

    // If still over limit, remove oldest entries
    const remainingEntries = Object.entries(routeLinesRef.current);
    if (remainingEntries.length > MAX_ROUTE_LINES) {
      const sortedByAge = remainingEntries.sort((a, b) =>
        (a[1].timestamp || 0) - (b[1].timestamp || 0)
      );

      const toRemove = sortedByAge.slice(0, remainingEntries.length - MAX_ROUTE_LINES);
      toRemove.forEach(([flightId]) => {
        removeRouteLine(map, flightId);
      });
    }
  }, [removeRouteLine]);

  // MEMORY LEAK FIX: Prune ball positions by age and size
  const pruneBallPositions = useCallback(() => {
    const now = Date.now();
    const MAX_BALL_POSITIONS = 50;  // Cap at 50 active ball positions
    const MAX_AGE_MS = 3 * 60 * 1000;  // Remove positions older than 3 minutes

    const entries = Object.entries(ballPositionsRef.current);

    // Remove old entries first
    entries.forEach(([flightId, data]) => {
      if (data.timestamp && (now - data.timestamp) > MAX_AGE_MS) {
        delete ballPositionsRef.current[flightId];
      }
    });

    // If still over limit, remove oldest entries
    const remainingEntries = Object.entries(ballPositionsRef.current);
    if (remainingEntries.length > MAX_BALL_POSITIONS) {
      const sortedByAge = remainingEntries.sort((a, b) =>
        (a[1].timestamp || 0) - (b[1].timestamp || 0)
      );

      const toRemove = sortedByAge.slice(0, remainingEntries.length - MAX_BALL_POSITIONS);
      toRemove.forEach(([flightId]) => {
        delete ballPositionsRef.current[flightId];
      });
    }
  }, []);

  // Initialize route lines when flights are added, remove when complete
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !map.getStyle()) return;

    // MEMORY LEAK FIX: Prune old and excess route lines and ball positions
    pruneRouteLines(map);
    pruneBallPositions();

    // Combine active flights and pending arrivals
    const activeIds = new Set(flightPositions.map(f => f.id));
    const pendingIds = new Set(Object.keys(pendingArrivals));

    // FLIGHTRADAR24 STYLE: Only show route line for selected flight
    // Remove all route lines except the selected one
    Object.keys(routeLinesRef.current).forEach(flightId => {
      if (flightId !== selectedFlightId) {
        const routeLayerId = `route-layer-${flightId}`;
        const routeSourceId = `route-${flightId}`;
        if (map.getLayer(routeLayerId)) {
          map.removeLayer(routeLayerId);
        }
        if (map.getSource(routeSourceId)) {
          map.removeSource(routeSourceId);
        }
        // Clean up endpoint markers too
        const endpointLayerId = `endpoints-layer-${flightId}`;
        const labelLayerId = `endpoints-labels-${flightId}`;
        const endpointSourceId = `endpoints-${flightId}`;
        if (map.getLayer(labelLayerId)) map.removeLayer(labelLayerId);
        if (map.getLayer(endpointLayerId)) map.removeLayer(endpointLayerId);
        if (map.getSource(endpointSourceId)) map.removeSource(endpointSourceId);
        delete routeLinesRef.current[flightId];
      }
    });

    // Add route line + endpoint markers for selected flight only
    if (selectedFlightId) {
      const flight = flightPositions.find(f => f.id === selectedFlightId);
      if (flight && flight.hasLaunched && !routeLinesRef.current[flight.id]) {
        addRouteLine(map, flight.id, flight.originLon, flight.originLat, flight.destLon, flight.destLat, flight.origin, flight.destination);

        // Add origin + destination endpoint markers
        const endpointSourceId = `endpoints-${flight.id}`;
        const endpointLayerId = `endpoints-layer-${flight.id}`;
        const labelLayerId = `endpoints-labels-${flight.id}`;
        if (!map.getSource(endpointSourceId)) {
          map.addSource(endpointSourceId, {
            type: 'geojson',
            data: {
              type: 'FeatureCollection',
              features: [
                { type: 'Feature', geometry: { type: 'Point', coordinates: [flight.originLon, flight.originLat] }, properties: { label: flight.origin || 'ORG', type: 'origin' } },
                { type: 'Feature', geometry: { type: 'Point', coordinates: [flight.destLon, flight.destLat] }, properties: { label: flight.destination || 'DST', type: 'destination' } },
              ]
            }
          });
          map.addLayer({
            id: endpointLayerId,
            type: 'circle',
            source: endpointSourceId,
            paint: {
              'circle-radius': 6,
              'circle-color': ['match', ['get', 'type'], 'origin', '#22c55e', '#ef4444'],
              'circle-stroke-width': 2,
              'circle-stroke-color': '#ffffff',
            }
          });
          map.addLayer({
            id: labelLayerId,
            type: 'symbol',
            source: endpointSourceId,
            layout: {
              'text-field': ['get', 'label'],
              'text-size': 13,
              'text-offset': [0, -1.5],
              'text-anchor': 'bottom',
              'text-font': ['DIN Pro Medium', 'Arial Unicode MS Regular'],
            },
            paint: {
              'text-color': '#ffffff',
              'text-halo-color': '#000000',
              'text-halo-width': 1.5,
            }
          });
        }
      }
    }
  }, [flightPositions, pendingArrivals, mapReady, selectedFlightId, addRouteLine, pruneRouteLines, pruneBallPositions]);

  // Get selected flight
  const selectedFlight = useMemo(() => {
    if (!selectedFlightId) return null;
    return flightPositions.find(f => f.id === selectedFlightId) || null;
  }, [selectedFlightId, flightPositions]);

  // Click handler for flight selection
  const handleFlightClick = useCallback((flight) => {
    setSelectedFlightId(flight.id);
    const map = mapRef.current;
    if (map) {
      map.flyTo({ center: [flight.currentLon, flight.currentLat], zoom: 7 });
    }
  }, []);

  // ESC to stop following
  useEffect(() => {
    if (!followingFlight) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setFollowingFlight(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [followingFlight]);

  // Sync selectedFlightId when following a flight (map marker click sets followingFlight, not selectedFlightId)
  useEffect(() => {
    if (followingFlight) {
      setSelectedFlightId(followingFlight.id);
    }
  }, [followingFlight]);

  // Camera follow is handled by SmoothAircraftMarkerMapbox's internal loop (setCenter at 60fps)
  // We only need to detect when the followed flight lands and stop following
  useEffect(() => {
    if (!followingFlight) return;

    // Check if the followed flight still exists
    const currentFlight = flightPositions.find(f => f.id === followingFlight.id);
    if (!currentFlight) {
      // Flight no longer exists (landed), stop following
      setFollowingFlight(null);
    }
  }, [followingFlight, flightPositions]);

  // Clear selected flight route line when that flight lands
  useEffect(() => {
    if (!selectedFlightId) return;
    const stillActive = flightPositions.find(f => f.id === selectedFlightId);
    if (!stillActive) {
      setSelectedFlightId(null);
    }
  }, [selectedFlightId, flightPositions]);

  const gameTime = new Date(worldState?.gameTime || Date.now());

  // ==========================================================================
  // LAUNCH ALL + DEPLOY IDLE - Quick actions from map
  // ==========================================================================

  const routes = worldState?.routes || [];
  const aircraft = worldState?.aircraft || [];
  const playerHubs = worldState?.ownedHubs || [];
  const playerHubSet = useMemo(() => new Set([worldState?.airline?.hub, ...playerHubs].filter(Boolean)), [worldState?.airline?.hub, playerHubs]);

  // Find ready routes (have aircraft assigned, not in flight)
  const readyRoutes = useMemo(() => {
    return routes.filter(route => {
      if (!route.aircraftId) return false;
      // Check if aircraft is already flying this route
      const inFlight = activeFlights.some(f => f.routeId === route.id);
      return !inFlight;
    });
  }, [routes, activeFlights]);

  // Find idle aircraft at hubs and unassigned routes
  // Count is based on how many routes actually have matching aircraft at their origin
  const deployableInfo = useMemo(() => {
    const assignedIds = new Set(routes.map(r => r.aircraftId).filter(Boolean));
    const unassignedRoutes = routes.filter(r => !r.aircraftId);
    const idleAtHub = aircraft.filter(ac => {
      if (assignedIds.has(ac.id)) return false;
      const location = ac.location || 'KPDX';
      return playerHubSet.has(location);
    });

    // Calculate actual deployable count by simulating deployment
    // This matches the logic in handleDeployIdle
    const usedAircraft = new Set();
    let actualDeployable = 0;
    for (const route of unassignedRoutes) {
      const legOrigin = route.currentLegOrigin || route.origin;
      const suitable = idleAtHub.find(ac => {
        if (usedAircraft.has(ac.id)) return false;
        return (ac.location || 'KPDX') === legOrigin;
      });
      if (suitable) {
        usedAircraft.add(suitable.id);
        actualDeployable++;
      }
    }

    return {
      count: actualDeployable,
      idleAtHub,
      unassignedRoutes,
    };
  }, [routes, aircraft, playerHubSet]);

  // Launch all ready routes
  const handleLaunchAll = useCallback(() => {
    const now = new Date(worldState?.gameTime || Date.now());
    const currentDay = worldState?.dayNumber || 1;
    const currentHour = now.getHours();
    let launched = 0;

    for (const route of readyRoutes) {
      try {
        const ac = aircraft.find(a => a.id === route.aircraftId);
        if (!ac) continue;

        let legOrigin = route.currentLegOrigin || route.origin;
        let legDestination = route.currentLegDestination || route.destination;
        if (!legOrigin || !legDestination) continue;

        // Calculate basic flight parameters
        const distance = calculateDistance(legOrigin, legDestination);
        if (!distance || !Number.isFinite(distance)) continue;

        // =================================================================
        // ANOMALOUS BEHAVIOR CHECK - Process special aircraft behaviors
        // =================================================================
        const behaviorId = ac.eosId || ac.aircraftId || ac.id;
        let flightAnimation = null;
        let instantComplete = false;
        let durationMultiplier = 1;
        let revenueMultiplier = 1;
        let extraFlightData = {};

        if (hasAnomalousBehavior(behaviorId)) {
          const gameState = {
            currentDay,
            currentHour,
            allAirports: Object.keys(GLOBAL_AIRPORTS || {}),
            aircraftUses: worldState?.aircraftUses || {},
          };

          const flightData = {
            origin: legOrigin,
            destination: legDestination,
            distance,
          };

          const anomalyResult = processAnomalousBehavior(behaviorId, 'dispatch', flightData, gameState);
          debug('[WorldMap LaunchAll] anomalyResult for', behaviorId, ':', anomalyResult);

          // Check if dispatch is blocked
          if (anomalyResult.blocked) {
            debug(`[WorldMap] Anomalous aircraft ${behaviorId} blocked: ${anomalyResult.blockMessage}`);
            continue; // Skip this flight
          }

          // Apply modifications from anomalous behavior
          if (anomalyResult.flightData) {
            legOrigin = anomalyResult.flightData.origin || legOrigin;
            legDestination = anomalyResult.flightData.destination || legDestination;
            flightAnimation = anomalyResult.flightData.animation || null;
            instantComplete = anomalyResult.flightData.instantComplete || false;
            durationMultiplier = anomalyResult.flightData.durationMultiplier || 1;
            revenueMultiplier = anomalyResult.flightData.revenueMultiplier || 1;

            // Copy any extra anomalous properties
            if (anomalyResult.flightData.quantumState) extraFlightData.quantumState = true;
            if (anomalyResult.flightData.phantomCargo) extraFlightData.phantomCargo = true;
            if (anomalyResult.flightData.noWindows) extraFlightData.noWindows = true;
            if (anomalyResult.flightData.timeSlipActive) {
              extraFlightData.timeSlipActive = true;
              extraFlightData.timeSlipDisappearAt = anomalyResult.flightData.timeSlipDisappearAt;
              extraFlightData.timeSlipReappearOnDay = anomalyResult.flightData.timeSlipReappearOnDay;
            }
          }

          // Process side effects (toasts, modals, etc.)
          if (anomalyResult.sideEffects?.length > 0) {
            for (const effect of anomalyResult.sideEffects) {
              if (effect.type === 'toast') {
                toast({ title: 'Anomalous Event', description: effect.message, duration: 5000 });
              }
            }
          }
        }

        const flightTimeHours = calculateFlightTime(distance, ac.model) * durationMultiplier;
        const arrivalTime = new Date(now.getTime() + flightTimeHours * 3600000);

        // calculateEarnings expects a flight-like object
        const flightData = {
          origin: legOrigin,
          destination: legDestination,
          aircraftType: ac.model,
          aircraftId: ac.id,
        };
        const earnings = calculateEarnings(flightData, worldState) || {};

        createFlight({
          routeId: route.id,
          aircraftId: ac.id,
          aircraftType: ac.model,
          origin: legOrigin,
          destination: legDestination,
          registration: ac.registration,
          scheduledDeparture: now.toISOString(),
          scheduledArrival: arrivalTime.toISOString(),
          passengers: earnings.passengers || 0,
          estimatedRevenue: Math.round((earnings.revenue || 0) * revenueMultiplier),
          estimatedCost: earnings.costs || 0,
          immediateDepart: true, // Simple mode: take off immediately
          preCalculatedEarnings: {
            ...earnings,
            revenue: Math.round((earnings.revenue || 0) * revenueMultiplier),
          },
          // Anomalous properties
          animation: flightAnimation,
          behaviorId: behaviorId,
          eosId: ac.eosId || null,
          instantComplete: instantComplete,
          ...extraFlightData,
        });
        launched++;
      } catch (err) {
        console.error(`[WorldMap] Failed to launch route ${route?.id}:`, err);
      }
    }
    debug(`[WorldMap] Launched ${launched}/${readyRoutes.length} flights`);
  }, [readyRoutes, aircraft, createFlight, worldState]);

  // Deploy idle aircraft to unassigned routes
  const handleDeployIdle = useCallback(() => {
    const { idleAtHub, unassignedRoutes } = deployableInfo;
    const usedAircraft = new Set();
    let assigned = 0;

    for (const route of unassignedRoutes) {
      const legOrigin = route.currentLegOrigin || route.origin;
      const suitable = idleAtHub.find(ac => {
        if (usedAircraft.has(ac.id)) return false;
        return (ac.location || 'KPDX') === legOrigin;
      });

      if (suitable) {
        assignAircraftToRoute(route.id, suitable.id);
        usedAircraft.add(suitable.id);
        assigned++;
      }
    }
    debug(`[WorldMap] Deployed ${assigned} idle aircraft`);
  }, [deployableInfo, assignAircraftToRoute]);

  // Calculate ETA
  const getETA = (flight) => {
    if (!flight || !flight.scheduledDeparture) return "--:--";
    const flightHours = calculateFlightTime(flight.distance, flight.aircraftType);
    const departureTime = new Date(flight.scheduledDeparture);
    const eta = new Date(departureTime.getTime() + flightHours * 3600000 + (flight.totalDelayMinutes || 0) * 60000);
    return formatTime(eta);
  };

  // Calculate time remaining
  const getTimeRemaining = (flight) => {
    if (!flight) return "--";
    const flightHours = calculateFlightTime(flight.distance, flight.aircraftType);
    const remainingHours = flightHours * (1 - getBallProgress(flight));
    const mins = Math.round(remainingHours * 60);
    if (mins < 60) return `${mins} min`;
    return `${Math.floor(mins / 60)}h ${mins % 60}m`;
  };

  // News ticker items - weather, flights, events
  const newsItems = useMemo(() => {
    const items = [];
    const flights = worldState?.flights || [];
    const airportWeather = worldState?.airportWeather || {};

    // Weather alerts
    for (const [code, weather] of Object.entries(airportWeather)) {
      const impact = weather?.impact;
      if (impact?.severity === 'grounded') {
        items.push({ type: 'critical', text: `WEATHER: ${code} GROUND STOP - ${impact.reason}` });
      } else if (impact?.severity === 'delay' && impact.delayMinutes >= 30) {
        items.push({ type: 'warning', text: `WEATHER: ${code} DELAY +${impact.delayMinutes}min - ${impact.reason}` });
      }
    }

    // Recent completions
    const completed = flights.filter(f => f.phase === "completed" && f.revenue).slice(0, 5);
    for (const f of completed) {
      const profit = (f.revenue || 0) - (f.costs || 0);
      items.push({
        type: profit >= 0 ? "profit" : "loss",
        text: `${f.flightNumber} ${f.origin}-${f.destination}: ${profit >= 0 ? '+' : ''}$${(profit/1000).toFixed(1)}K`,
      });
    }

    // Delays
    const delayed = flights.filter(f => f.totalDelayMinutes > 15 && f.phase !== "completed").slice(0, 3);
    for (const f of delayed) {
      items.push({ type: "warning", text: `DELAY: ${f.flightNumber} +${f.totalDelayMinutes}min` });
    }

    // News feed
    const newsFeed = worldState?.newsFeed || [];
    for (const news of newsFeed.slice(0, 5)) {
      items.push({ type: news.severity || "info", text: news.headline || news.message });
    }

    // Fallback
    if (items.length < 3) {
      items.push({ type: "info", text: "Operations running smoothly" });
      items.push({ type: "info", text: "Monitor weather for route planning" });
    }

    return items;
  }, [worldState?.flights, worldState?.airportWeather, worldState?.newsFeed]);

  // Loading guard
  if (!worldState) {
    return (
      <div className="h-screen flex items-center justify-center" style={{ backgroundColor: "#0a0a0a", color: "#e5e5e5" }}>
        Loading...
      </div>
    );
  }

  return (
    <>
      {/* Anomalous UI overlays */}
      <FakeBSOD
        isOpen={bsodState.isOpen}
        onClose={bsodState.hideBSOD}
        text={bsodState.text}
      />
      <TimelineForkModal
        isOpen={timelineForkState.isOpen}
        onClose={timelineForkState.hideFork}
        onChoice={timelineForkState.handleChoice}
        choiceA={timelineForkState.choiceA}
        choiceB={timelineForkState.choiceB}
        message={timelineForkState.message}
      />
      <AnomalyModal
        anomaly={anomalyEvent}
        onClose={() => setAnomalyEvent(null)}
      />

      <div className="h-screen flex flex-col overflow-hidden" style={{ backgroundColor: "#0a0a0a" }}>
      {/* Header - Clock centered, controls spread */}
      <div
        className="relative flex items-center px-4 py-2 border-b"
        style={{ backgroundColor: "#0c0c0c", borderColor: "#1a1a1a", zIndex: 1000 }}
      >
        {/* LEFT - Title + Time Controls */}
        <div className="flex items-center gap-6">
          <h1 className="text-xl font-bold" style={{ color: "#60ffd0" }}>
            {t('map.title')}
          </h1>

          {/* Time Controls */}
          <div className="flex gap-1">
            <Button
              size="sm"
              variant={worldState.isPaused ? "default" : "outline"}
              onClick={togglePause}
              style={worldState.isPaused ? { backgroundColor: "#ef4444" } : { borderColor: "#1a1a1a" }}
            >
              {worldState.isPaused ? "▶" : "⏸"}
            </Button>
            {/* Speed options: 1x,2x,4x always available | 12x unlocked with EOS */}
            {(() => {
              const unlocks = worldState?.unlocks || {};
              const speeds = [1, 2, 4]; // 4x always available from start
              if (unlocks.speed12x) speeds.push(12); // 12x unlocked with EOS
              return speeds.map((speed) => (
                <Button
                  key={speed}
                  size="sm"
                  variant={worldState.gameSpeed === speed ? "default" : "outline"}
                  onClick={() => setGameSpeed(speed)}
                  style={
                    worldState.gameSpeed === speed
                      ? { backgroundColor: "#60ffd0", color: "#000" }
                      : { borderColor: "#1a1a1a" }
                  }
                >
                  {speed}x
                </Button>
              ));
            })()}
          </div>

          {/* Weather Toggle */}
          <Button
            size="sm"
            variant={showWeather ? "default" : "outline"}
            onClick={() => setShowWeather(!showWeather)}
            style={
              showWeather
                ? { backgroundColor: "#3b82f6", color: "#fff" }
                : { borderColor: "#1a1a1a" }
            }
            title="Toggle Weather Radar"
          >
            {showWeather ? t('map.wxOn') : t('map.wxOff')}
          </Button>

          {/* Quick Actions - Launch/Deploy */}
          <div className="flex gap-1 ml-2 pl-2" style={{ borderLeft: "1px solid #333" }}>
            <Button
              size="sm"
              onClick={handleLaunchAll}
              disabled={readyRoutes.length === 0}
              style={{
                backgroundColor: readyRoutes.length > 0 ? "#22c55e" : "#1a1a1a",
                color: readyRoutes.length > 0 ? "#000" : "#525252",
              }}
              title="Launch all ready flights"
            >
              {t('map.launch')} ({readyRoutes.length})
            </Button>
            <Button
              size="sm"
              onClick={handleDeployIdle}
              disabled={deployableInfo.count === 0}
              style={{
                backgroundColor: deployableInfo.count > 0 ? "#3b82f6" : "#1a1a1a",
                color: deployableInfo.count > 0 ? "#fff" : "#525252",
              }}
              title="Assign idle aircraft to unassigned routes"
            >
              {t('map.deploy')} ({deployableInfo.count})
            </Button>
          </div>
        </div>

        {/* CENTER - Retro LED Clock (absolutely centered) */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <div className="px-4 py-2 rounded" style={{
            backgroundColor: "#0a0a0a",
            border: "2px solid #1a1a1a",
            boxShadow: "inset 0 2px 8px rgba(0,0,0,0.8)"
          }}>
            <div style={{
              fontFamily: "'Segment7', 'DSEG7 Classic', 'Courier New', monospace",
              fontSize: "1.5rem",
              fontWeight: "bold",
              color: "#ff2020",
              textShadow: "0 0 10px #ff2020, 0 0 20px #ff2020",
              letterSpacing: "2px",
              lineHeight: 1,
            }}>
              {(() => {
                const hours = gameTime.getHours();
                const minutes = gameTime.getMinutes();
                const displayHours = hours % 12 || 12;
                const ampm = hours >= 12 ? 'PM' : 'AM';
                return `${displayHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${ampm}`;
              })()}
            </div>
            <div className="text-xs text-center mt-1" style={{ color: "#ff2020", opacity: 0.7 }}>
              {gameTime.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })} | DAY {worldState.dayNumber || 1}
            </div>
          </div>
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* RIGHT - Fuel Ticker + Quick Nav Buttons */}
        <div className="flex items-center gap-3">
          {/* Fuel Price Ticker - shows chaos when Market Maker active */}
          <FuelPriceTicker
            fuelState={worldState?.fuel}
            chaos={worldState?.fuelMarketChaos}
          />
          <Button size="sm" onClick={() => navigate("/opscenter")} style={{ backgroundColor: "#60ffd0", color: "#000" }}>
            {t('map.opsCenter')}
          </Button>
          <Button size="sm" onClick={() => navigate("/AircraftMarket")} style={{ backgroundColor: "#f59e0b", color: "#000" }}>
            {t('map.aircraft')}
          </Button>
          <Button size="sm" onClick={() => navigate("/financials")} style={{ backgroundColor: "#22c55e", color: "#000" }}>
            {t('map.financials')}
          </Button>
          {canManuallyEndDay(worldState) && !worldState.isPaused && (
            <Button
              size="sm"
              onClick={() => {
                setWorldState(prev => ({
                  ...prev,
                  pendingEndOfDay: true,
                  isPaused: true,
                }));
                navigate("/opscenter");
              }}
              style={{ backgroundColor: '#f0f', color: '#000' }}
            >
              {t('map.endDay')}
            </Button>
          )}
        </div>
      </div>

      {/* Map Area - overflow hidden prevents page scroll */}
      <div className="flex-1 flex overflow-hidden">
        {/* Mapbox Map */}
        <div className="flex-1 relative overflow-hidden">
          {/* Empty Fleet Overlay */}
          {(!worldState?.aircraft || worldState.aircraft.length === 0) && (
            <div
              className="absolute inset-0 flex items-center justify-center"
              style={{ backgroundColor: "rgba(0, 0, 0, 0.85)", zIndex: 1000 }}
            >
              <div className="text-center p-8 rounded-lg" style={{ backgroundColor: "#1a1a1a", border: "2px solid #f59e0b", maxWidth: "500px" }}>
                <h2 className="text-2xl font-bold mb-2" style={{ color: "#f59e0b" }}>
                  {t('map.emptyFleet')}
                </h2>
                <p className="mb-4" style={{ color: "#a3a3a3" }}>
                  <span style={{ color: "#22c55e", fontWeight: "bold" }}>${((worldState?.airline?.cash || 0) / 1000000).toFixed(1)}M</span> {t('map.startingCapital')}
                </p>
                <p className="mb-6" style={{ color: "#737373" }}>
                  {t('map.visitMarket')}
                </p>
                <Button
                  size="lg"
                  onClick={() => navigate("/AircraftMarket")}
                  style={{ backgroundColor: "#f59e0b", color: "#000", fontSize: "1.1rem", padding: "1rem 2rem" }}
                >
                  {t('map.goToMarket')}
                </Button>
              </div>
            </div>
          )}

          {/* Map Container */}
          <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />

          {/* Smooth Aircraft Markers Overlay - positioned over map */}
          {mapReady && mapRef.current && (
            <div
              ref={overlayRef}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 10,
              }}
            >
              {/* Active flights - use hasLaunched to keep marker stable even if phase glitches */}
              {/* Exclude timeSlipped flights (Agee-Whells) and invisible flights (Blackout) */}
              {flightPositions
                .filter(f => f.hasLaunched && f.phase !== "completed" && f.status !== "completed" && !f.timeSlipped && !f.invisibleOnMap)
                .map((flight) => (
                <SmoothAircraftMarkerMapbox
                  key={flight.id}
                  map={mapRef.current}
                  originLat={flight.originLat}
                  originLon={flight.originLon}
                  destLat={flight.destLat}
                  destLon={flight.destLon}
                  flightDurationMs={(flight.distance / (AIRCRAFT_DATABASE[flight.aircraftType]?.cruiseSpeed || 450)) * 3600000 / 4.5}
                  gameSpeed={worldState.gameSpeed}
                  isPaused={worldState.isPaused}
                  flight={flight}
                  onFollow={setFollowingFlight}
                  isFollowing={followingFlight?.id === flight.id}
                  onArrived={handleBallArrived}
                  onPositionUpdate={handlePositionUpdate}
                                  />
              ))}
              {/* Pending arrivals - flights removed from game state but ball hasn't reached destination */}
              {Object.values(pendingArrivals).map((flight) => (
                <SmoothAircraftMarkerMapbox
                  key={`pending-${flight.id}`}
                  map={mapRef.current}
                  originLat={flight.originLat}
                  originLon={flight.originLon}
                  destLat={flight.destLat}
                  destLon={flight.destLon}
                  flightDurationMs={(flight.distance / (AIRCRAFT_DATABASE[flight.aircraftType]?.cruiseSpeed || 450)) * 3600000 / 4.5}
                  gameSpeed={worldState.gameSpeed}
                  isPaused={worldState.isPaused}
                  flight={flight}
                  onFollow={setFollowingFlight}
                  isFollowing={followingFlight?.id === flight.id}
                  onArrived={handleBallArrived}
                  onPositionUpdate={handlePositionUpdate}
                                  />
              ))}

              {/* Zero Divided by Zero ghost markers - positioned via map projection */}
              {mapRef.current && zeroDividedLocations.map((location, idx) => {
                const point = mapRef.current.project([location.lon, location.lat]);
                const statuses = ['Departed', 'Delayed', 'Cancelled', 'Arrived'];
                const statusText = statuses.join(' • ');
                return (
                  <div
                    key={`zero-divided-${idx}`}
                    style={{
                      position: 'absolute',
                      left: `${point.x}px`,
                      top: `${point.y}px`,
                      transform: 'translate(-50%, -50%)',
                      pointerEvents: 'none',
                      zIndex: 50,
                    }}
                  >
                    {/* Glitchy aircraft icon */}
                    <div
                      style={{
                        fontSize: '24px',
                        opacity: 0.6,
                        color: '#ff00ff',
                        textShadow: '0 0 8px #ff00ff, 2px 0 #00ffff, -2px 0 #ffff00',
                        animation: 'pulse 1s ease-in-out infinite',
                      }}
                    >
                      ?
                    </div>
                    {/* All statuses at once */}
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        whiteSpace: 'nowrap',
                        fontSize: '9px',
                        color: '#ff00ff',
                        opacity: 0.8,
                        textShadow: '1px 0 #00ffff, -1px 0 #ffff00',
                        marginTop: '2px',
                      }}
                    >
                      {statusText}
                    </div>
                    {/* Location */}
                    <div
                      style={{
                        position: 'absolute',
                        bottom: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        whiteSpace: 'nowrap',
                        fontSize: '8px',
                        color: '#a1a1a1',
                        opacity: 0.6,
                        marginBottom: '2px',
                      }}
                    >
                      0/∞ @ {location.code}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Style Switcher removed - alternate Mapbox styles crash the game */}

        </div>

        {/* Right Panel - scrolls internally, doesn't affect page */}
        <div
          className="w-96 border-l overflow-y-auto flex-shrink-0"
          style={{ backgroundColor: "#0c0c0c", borderColor: "#1a1a1a", maxHeight: "100%" }}
        >
          <div className="p-4">
            <h2 className="text-lg font-bold mb-4" style={{ color: "#60ffd0" }}>
              {t('map.activeFlights')} ({activeFlights.length})
            </h2>

            {activeFlights.length === 0 ? (
              <div className="text-center py-8" style={{ color: "#737373" }}>
                <p className="text-2xl opacity-30 mb-2">--</p>
                <p>{t('map.noActiveFlights')}</p>
                <Button
                  size="sm"
                  className="mt-4"
                  onClick={() => navigate("/routes")}
                  style={{ backgroundColor: "#60ffd0", color: "#000" }}
                >
                  {t('map.createFlight')}
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {flightPositions.map((flight) => {
                  const isSelected = selectedFlightId === flight.id;
                  return (
                    <div key={flight.id}>
                      {/* Flight card */}
                      <div
                        className="p-3 rounded cursor-pointer transition-all"
                        style={{
                          backgroundColor: isSelected ? "#0d2818" : "#111111",
                          border: isSelected ? "1px solid #60ffd0" : "1px solid transparent",
                          borderBottomLeftRadius: isSelected ? 0 : undefined,
                          borderBottomRightRadius: isSelected ? 0 : undefined,
                        }}
                        onClick={() => handleFlightClick(flight)}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-bold font-mono" style={{ color: "#e5e5e5" }}>{flight.flightNumber}</span>
                          <Badge
                            style={{
                              backgroundColor: ["takeoff", "climb", "cruise", "descent", "approach", "landing"].includes(getBallPhase(flight)) ? "#2563eb" : getBallPhase(flight) === "arrived" ? "#22c55e" : "#eab308",
                              color: "#fff",
                            }}
                          >
                            {phaseNames[getBallPhase(flight)] || getBallPhase(flight)}
                          </Badge>
                        </div>

                        <div className="text-sm mb-2" style={{ color: "#a3a3a3" }}>
                          {flight.origin} - {flight.destination}
                        </div>

                        {/* Progress bar */}
                        <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: "#0a0a0a" }}>
                          <div
                            className="h-full transition-all"
                            style={{
                              width: `${Math.round(getBallProgress(flight) * 100)}%`,
                              backgroundColor: "#60ffd0",
                            }}
                          />
                        </div>

                        <div className="flex items-center justify-between text-xs mt-1" style={{ color: "#737373" }}>
                          <span>{Math.round(getBallProgress(flight) * 100)}%</span>
                          {flight.totalDelayMinutes > 0 && (
                            <span style={{ color: "#ef4444" }}>
                              +{flight.totalDelayMinutes}m {t('map.delay')}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* INLINE detail panel - accordion style */}
                      {isSelected && (
                        <div
                          className="p-3 rounded-b border-t-0"
                          style={{
                            backgroundColor: "#0a0a0a",
                            border: "1px solid #60ffd0",
                            borderTop: "none",
                          }}
                        >
                          {/* Route info */}
                          <div className="grid grid-cols-3 gap-2 text-xs mb-3">
                            <div>
                              <div style={{ color: "#737373" }}>Distance</div>
                              <div className="font-mono" style={{ color: "#e5e5e5" }}>{flight.distance} nm</div>
                            </div>
                            <div>
                              <div style={{ color: "#737373" }}>Remaining</div>
                              <div className="font-mono" style={{ color: "#e5e5e5" }}>{Math.round(flight.distance * (1 - getBallProgress(flight)))} nm</div>
                            </div>
                            <div>
                              <div style={{ color: "#737373" }}>Bearing</div>
                              <div className="font-mono" style={{ color: "#e5e5e5" }}>{Math.round(flight.bearing)}°</div>
                            </div>
                          </div>

                          {/* Speed/Alt */}
                          <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                            <div>
                              <div style={{ color: "#737373" }}>Ground Speed</div>
                              <div className="font-mono" style={{ color: "#e5e5e5" }}>{flight.groundSpeed} kts</div>
                            </div>
                            <div>
                              <div style={{ color: "#737373" }}>Altitude</div>
                              <div className="font-mono" style={{ color: "#e5e5e5" }}>
                                {flight.altitude > 18000
                                  ? `FL${Math.round(flight.altitude / 100)}`
                                  : `${formatNumber(flight.altitude)} ft`}
                              </div>
                            </div>
                          </div>

                          {/* Times */}
                          <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                            <div>
                              <div style={{ color: "#737373" }}>ETA</div>
                              <div className="font-mono" style={{ color: "#e5e5e5" }}>{getETA(flight)}</div>
                            </div>
                            <div>
                              <div style={{ color: "#737373" }}>Time Left</div>
                              <div className="font-mono" style={{ color: "#60ffd0" }}>{getTimeRemaining(flight)}</div>
                            </div>
                          </div>

                          {/* Aircraft */}
                          <div className="text-xs pt-2 border-t" style={{ borderColor: "#1a1a1a", color: "#737373" }}>
                            {flight.aircraftRegistration || "N/A"} | {flight.aircraftType || "E175"}
                          </div>

                          {flight.totalDelayMinutes > 0 && (
                            <div className="mt-2 text-xs font-bold" style={{ color: "#ef4444" }}>
                              +{flight.totalDelayMinutes} min delay
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* OLD: Selected flight details panel at bottom - REMOVED, now inline above */}
          {false && selectedFlight && (
            <div className="border-t p-4" style={{ borderColor: "#1a1a1a" }}>
              {/* Flight Header */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold font-mono" style={{ color: "#60ffd0" }}>
                    {selectedFlight.flightNumber}
                  </h3>
                  <div className="text-sm" style={{ color: "#737373" }}>
                    {AIRPORTS[selectedFlight.origin]?.city || selectedFlight.origin} - {AIRPORTS[selectedFlight.destination]?.city || selectedFlight.destination}
                  </div>
                </div>
                <Badge
                  className="text-sm px-3 py-1"
                  style={{
                    backgroundColor: ["takeoff", "climb", "cruise", "descent", "approach", "landing"].includes(getBallPhase(selectedFlight)) ? "#2563eb" : "#eab308",
                    color: "#fff",
                  }}
                >
                  {phaseNames[getBallPhase(selectedFlight)]}
                </Badge>
              </div>

              {/* Route Section */}
              <div className="mb-4 p-3 rounded" style={{ backgroundColor: "#111" }}>
                <div className="flex items-center gap-2 mb-2" style={{ color: "#60ffd0" }}>
                  <span className="text-xs font-bold uppercase">Route</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <div style={{ color: "#737373" }}>Distance</div>
                    <div className="font-mono" style={{ color: "#e5e5e5" }}>{selectedFlight.distance} nm</div>
                  </div>
                  <div>
                    <div style={{ color: "#737373" }}>Remaining</div>
                    <div className="font-mono" style={{ color: "#e5e5e5" }}>{Math.round(selectedFlight.distance * (1 - getBallProgress(selectedFlight)))} nm</div>
                  </div>
                  <div>
                    <div style={{ color: "#737373" }}>Progress</div>
                    <div className="font-mono" style={{ color: "#60ffd0" }}>{Math.round(getBallProgress(selectedFlight) * 100)}%</div>
                  </div>
                  <div>
                    <div style={{ color: "#737373" }}>Heading</div>
                    <div className="font-mono" style={{ color: "#e5e5e5" }}>{Math.round(selectedFlight.bearing)}°</div>
                  </div>
                </div>
              </div>

              {/* Performance Section */}
              <div className="mb-4 p-3 rounded" style={{ backgroundColor: "#111" }}>
                <div className="flex items-center gap-2 mb-2" style={{ color: "#60ffd0" }}>
                  <Gauge className="w-4 h-4" />
                  <span className="text-xs font-bold uppercase">Performance</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <div style={{ color: "#737373" }}>Ground Speed</div>
                    <div className="font-mono" style={{ color: "#e5e5e5" }}>{selectedFlight.groundSpeed} kts</div>
                  </div>
                  <div>
                    <div style={{ color: "#737373" }}>Altitude</div>
                    <div className="font-mono" style={{ color: "#e5e5e5" }}>
                      {selectedFlight.altitude > 18000
                        ? `FL${Math.round(selectedFlight.altitude / 100)}`
                        : `${formatNumber(selectedFlight.altitude)} ft`}
                    </div>
                  </div>
                </div>
              </div>

              {/* Fuel Section */}
              {selectedFlight.fuel && (
              <div className="mb-4 p-3 rounded" style={{ backgroundColor: "#111" }}>
                <div className="flex items-center gap-2 mb-2" style={{ color: "#60ffd0" }}>
                  <Fuel className="w-4 h-4" />
                  <span className="text-xs font-bold uppercase">Fuel</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <div style={{ color: "#737373" }}>Burn Rate</div>
                    <div className="font-mono" style={{ color: "#e5e5e5" }}>{formatNumber(selectedFlight.fuel.burnRate || 0)} lbs/hr</div>
                  </div>
                  <div>
                    <div style={{ color: "#737373" }}>Used</div>
                    <div className="font-mono" style={{ color: "#e5e5e5" }}>{formatNumber(selectedFlight.fuel.used || 0)} lbs</div>
                  </div>
                  <div className="col-span-2">
                    <div style={{ color: "#737373" }}>Remaining</div>
                    <div className="font-mono text-lg" style={{ color: "#22c55e" }}>{formatNumber(selectedFlight.fuel.remaining || 0)} lbs</div>
                  </div>
                </div>
              </div>
              )}

              {/* Time Section */}
              <div className="mb-4 p-3 rounded" style={{ backgroundColor: "#111" }}>
                <div className="flex items-center gap-2 mb-2" style={{ color: "#60ffd0" }}>
                  <span className="text-xs font-bold uppercase">Time</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <div style={{ color: "#737373" }}>Departed</div>
                    <div className="font-mono" style={{ color: "#e5e5e5" }}>
                      {selectedFlight.scheduledDeparture
                        ? formatTime(new Date(selectedFlight.scheduledDeparture))
                        : "--:--"}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: "#737373" }}>ETA</div>
                    <div className="font-mono" style={{ color: "#e5e5e5" }}>{getETA(selectedFlight)}</div>
                  </div>
                  <div className="col-span-2">
                    <div style={{ color: "#737373" }}>Time Remaining</div>
                    <div className="font-mono text-lg" style={{ color: "#60ffd0" }}>{getTimeRemaining(selectedFlight)}</div>
                  </div>
                </div>
              </div>

              {/* Aircraft Section */}
              <div className="p-3 rounded" style={{ backgroundColor: "#111" }}>
                <div className="flex items-center gap-2 mb-2" style={{ color: "#60ffd0" }}>
                  <span className="text-xs font-bold uppercase">Aircraft</span>
                </div>
                <div className="text-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-mono font-bold" style={{ color: "#e5e5e5" }}>
                      {selectedFlight.aircraftRegistration || "N/A"} | {selectedFlight.aircraftType || "E175"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Delay Warning */}
              {selectedFlight.totalDelayMinutes > 0 && (
                <div className="mt-4 p-3 rounded" style={{ backgroundColor: "#2a0a0a", border: "1px solid #ef4444" }}>
                  <div className="flex items-center justify-between">
                    <span style={{ color: "#fca5a5" }}>Total Delay</span>
                    <span className="font-mono font-bold" style={{ color: "#ef4444" }}>
                      +{selectedFlight.totalDelayMinutes} min
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* News Ticker - Same as OpsCenter */}
      <NewsTicker worldState={worldState} />

      {/* Hub Offer Modal (Simple mode only) - Exclusive for first 3 hubs, then auction */}
      {showHubAuction && hubOfferType === 'exclusive' && (
        <ExclusiveHubOffer
          hub={showHubAuction}
          trigger={hubOfferTrigger}
          onClose={handleHubAuctionClose}
          onComplete={handleHubAuctionComplete}
        />
      )}
      {showHubAuction && hubOfferType === 'auction' && (
        <SimpleHubAuction
          hub={showHubAuction}
          onClose={handleHubAuctionClose}
          onComplete={handleHubAuctionComplete}
        />
      )}

      {/* Route Creation Modal - triggered from map airport popup */}
      <NewRouteModal
        isOpen={!!routeCreationAirport}
        onClose={() => setRouteCreationAirport(null)}
        onCreateRoute={() => setRouteCreationAirport(null)}
        initialDestination={routeCreationAirport}
      />
      </div>
    </>
  );
}
